//@ts-ignore
//@ts-nocheck

/* ==========================================================================
   Wizzy Search — overview
   ==========================================================================
   This file drives two surfaces that share the same state/cache machinery:

   1. Header search (modal): `snippets/neo-search-modal.liquid` (`.neo-search-container`).
      Clicking `.neo-search-button` adds `.active`, which is all CSS needs to
      animate the whole UI open — no inline styles or layout reads from JS.
      Typing debounces into `sendAutocompleteRequest()`, which hits
      `/v1/autocomplete` and renders a small product list + suggestion
      pills; `.full-width` is added to `.neo-search-modal-container` once
      there's a query, revealing that results view. Hitting Enter (or
      clicking a trending/recent `<a href>`) navigates to `/search?q=...`.

   2. `/search` results page: full grid with facets, sort, price range and
      infinite scroll. All of it funnels through one function —
      `createFilterSearchQuery()` — which decides whether the lightweight
      `/products/search` endpoint (query only) or the heavier
      `/products/filter` endpoint (facets/sort/price/pagination) is needed,
      then fetches (or reuses a cached/prefetched response) and hands the
      payload to `applyWizzyFilterResponse()` to hydrate the DOM.

   Companion files (loaded separately, referenced via `window.*`):
   - `snippets/wizzy-search-prefetch.liquid` fires the very first
     query-only `/products/search` call from an inline <head> script,
     before this file has even parsed, and stashes the result on
     `window.__wizzyData` / `window.__wizzySearchPrefetch`. This file's
     search-page bootstrap (bottom of file) tries to reuse that instead of
     re-fetching — see `wizzyFetchProductResponse()` and
     `wizzyTryApplyPrefetchSearch()`.
   - `snippets/wizzy-product-card-render.liquid` exposes
     `window.WizzyProductCards.render()/createElement()`, the actual card
     markup. This file never builds card HTML itself; it only fetches data
     and calls into that renderer (see `renderAutocompleteProducts()`).

   Caching (all in-memory, cleared on full reload):
   - `wizzyInFlightProductRequests` / `wizzyInFlightAutocomplete`: de-dupe
     identical concurrent requests (e.g. rapid filter clicks).
   - `wizzyProductResponseCache`: session LRU keyed by `apiMode:requestBody`
     so re-applying a previously-seen filter combo skips the network.
   - `wizzyBaselineSearchResponse` / head-prefetch `window.__wizzyData`:
     fallbacks used when the user clears all filters back to the original
     unfiltered query (see `wizzyTryApplyStoredProductResponse()`).
   ========================================================================== */

/* ==========================================================================
   Constants
   ========================================================================== */

const WIZZY_SEARCH_HISTORY_KEY = "wizzySearchQueryParamsHistory";
/** Query-string key for Wizzy searchedKey (survives reload for filter requests). */
const WIZZY_SEARCHED_KEY_PARAM = "searchedKey";

const WIZZY_API_BASE = "https://api.wizzy.ai/v1";
const WIZZY_AUTOCOMPLETE_MIN_QUERY_LENGTH = 1;
const WIZZY_AUTOCOMPLETE_DEBOUNCE_MS = 800;
const WIZZY_CACHE_DEBUG = false;
/** Search + filter page size — keep in sync with `snippets/wizzy-search-prefetch.liquid`. */
const WIZZY_PRODUCTS_COUNT = 12;

/** Autocomplete POST body — https://docs.api.wizzy.ai/#tag/Autocomplete */
const WIZZY_AUTOCOMPLETE_DEFAULTS = {
  currency: "INR",
  suggestionsCount: 10,
  includeOutOfStock: "true",
  getAllVariants: "false",
  minQueryLength: WIZZY_AUTOCOMPLETE_MIN_QUERY_LENGTH,
  productsCount: 6,
  swatch: "",
  sort: "",
  facets: "",
};

/** Must match `snippets/wizzy-search-prefetch.liquid` facet list (search + filter). */
const WIZZY_FACETS = {
  product_product_type: {
    label: "Category",
    key: "product_product_type",
    position: "left",
    order: 1,
  },
  sellingPrice: {
    label: "Price",
    key: "sellingPrice",
    position: "left",
    order: 2,
  },
  sizes: { label: "Size", key: "sizes", position: "left", order: 3 },
  colors: { label: "Color", key: "colors", position: "left", order: 4 },
  product_fabric_filter_custom: {
    label: "Fabric",
    key: "product_fabric_filter_custom",
    position: "left",
    order: 5,
  },
  product_activity_filter_neo: {
    label: "Activity",
    key: "product_activity_filter_neo",
    position: "left",
    order: 6,
  },
  product_coverage_filter_neo: {
    label: "Level of coverage",
    key: "product_coverage_filter_neo",
    position: "left",
    order: 7,
  },
  product_innovation_filter_neo: {
    label: "Innovation",
    key: "product_innovation_filter_neo",
    position: "left",
    order: 8,
  },
  product_goggles_straps_filter_neo: {
    label: "Goggles Straps",
    key: "product_goggles_straps_filter_neo",
    position: "left",
    order: 9,
  },
  product_flexibility_filter_neo: {
    label: "Flexibility",
    key: "product_flexibility_filter_neo",
    position: "left",
    order: 10,
  },
  product_fit_type_filter_custom: {
    label: "Fit Type",
    key: "product_fit_type_filter_custom",
    position: "left",
    order: 11,
  },
  product_gender_filter_custom: {
    label: "Gender",
    key: "product_gender_filter_custom",
    position: "left",
    order: 12,
  },
  product_lens_filter_custom: {
    label: "Lens Type",
    key: "product_lens_filter_custom",
    position: "left",
    order: 13,
  },
  product_usp_filter_neo: {
    label: "USPs",
    key: "product_usp_filter_neo",
    position: "left",
    order: 14,
  },
};

/** Skeleton vars — defined in assets/wizzy-product-cards.js (head). */
const WIZZY_SKELETON_STYLE =
  (typeof window !== "undefined" && window.WizzyProductCards?.SKELETON_STYLE) ||
  `
        --skeleton-width: 100%;
        --skeleton-height: 100%;
        --skeleton-min-height: 559px;
        --skeleton-radius: 13px;
        --skeleton-mt: 6px;
        --skeleton-bg: rgba(170, 170, 170,0.2);
        --skeleton-shine-color: rgba(255, 255, 255, 0.3);
        --skeleton-animation-duration: 1.7s;
      `;

/** Maps a facet's `label` (from the API response) to the DOM builder used in `filterFacetData()`. */
const FACET_BUILDERS = {
  Category: (facetData) => createCategoryElement(facetData),
  Size: (facetData) => createSizeElement(facetData),
  Color: (facetData) => createColorElement(facetData),
  Gender: (facetData) => createDefaultElement(facetData, "Gender"),
  Fabric: (facetData) => createDefaultElement(facetData, "Fabric"),
  "Fit Type": (facetData) => createDefaultElement(facetData, "Fit Type"),
  Flexibility: (facetData) => createDefaultElement(facetData, "Flexibility"),
  Innovation: (facetData) => createDefaultElement(facetData, "Innovation"),
  "Lens Type": (facetData) => createDefaultElement(facetData, "Lens Type"),
  USPs: (facetData) => createDefaultElement(facetData, "USPs"),
  "Goggles Straps": (facetData) => createDefaultElement(facetData, "Goggles Straps"),
  Price: (facetData) => createFilterPrice(facetData),
};

/* ==========================================================================
      State
      ========================================================================== */
      // Module-level (not per-instance) because there is only ever one active
      // search modal + one /search page per document. `modalRightPanel` is the
      // key mode switch used throughout: "input" = user is typing in the
      // header/mobile modal (autocomplete flow), "search" = the /search
      // results page (filter/sort/pagination flow). Helpers like
      // getProductContainer()/getRightPanel()/getProductGridContainer() branch
      // on it so the same rendering code serves both surfaces.

/** min/max = facet bounds from the API; currentMin/currentMax = user's slider selection. */
let wizzyPriceState = {
  min: 0,
  max: 0,
  currentMin: 0,
  currentMax: 0,
};

/** Active sort — "relevance"/asc is the default (no sort param sent to the API). */
let sorting = {
  state: "relevance",
  order: "asc",
};

/** pageCount = next page to request (increments on infinite scroll); payloadPageCount = total pages available from the last response. */
let page = {
  payloadPageCount: 1,
  pageCount: 1,
};

/** Selected facet values, split by whether they need the `attributes` (parentKey) or top-level (arraysObj) shape in the filter body — see `createFilterRequest()`. */
let arraysObj = {};
let wizzyAttributes = {};
let totalProductCount = 0;
let isPrice = false;
let isSorting = false;
let sourceHitRequest = "search";
/** Active query text, kept in sync with the URL's `q` param on /search. */
let searchQuery = "";
/** From search/filter response — must be sent on subsequent filter requests. */
let wizzySearchedKey = "";
let showClearAll = 0;
let hasAppliedRefreshFilters = false;
let modalRightPanel = "search";
let trendingList = [];

/** Bumped when header/modal autocomplete clears; in-flight responses compare `autocompleteGen` from `getInputValue`. */
let wizzyAutocompleteGeneration = 0;

/** Last hydrated result for the active request (q, page, sort, filters). */
let wizzyActiveFilterState = { filtersKey: "", response: null };

/** Unfiltered page-1 search response — restored when all filters are cleared. */
let wizzyBaselineSearchKey = "";
let wizzyBaselineSearchResponse = null;

/** Session trending queries — cleared on full page reload. */
let wizzyTrendingCache = null;
let wizzyTrendingInFlight = null;

/** Cached DOM refs — populated once on DOMContentLoaded via initWizzyDomCache(). */
const wizzyDom = {
  neoSearchContainer: null,
  neoOpenButton: null,
  neoDesktopInput: null,
  neoDesktopSubmitBtn: null,
  neoDesktopCloseBtn: null,
  neoMobileInput: null,
  neoMobileSubmitBtn: null,
  neoMobileCloseBtn: null,
  neoModalOverlay: null,
  neoProductContainer: null,
  searchModal: null,
  modalLeftPanel: null,
  modalRightPanel: null,
  searchProductGrid: null,
  searchRightPanel: null,
  searchProductContainer: null,
  searchProductGridContainer: null,
  recentList: null,
  recentContainer: null,
  trendingUl: null,
  filtersContainer: null,
  productCountContainer: null,
  clearAllBtn: null,
  priceRange: null,
  filterAside: null,
  mobileSortOverlay: null,
  mobileSortDrawer: null,
  searchPrefetchContainer: null,
};

/* ==========================================================================
  Cache layer
========================================================================== */
// Three independent mechanisms, checked in this order by
// `wizzyTryApplyStoredProductResponse()` before any network call is made:
//   1. `wizzyProductResponseCache` — exact same request body seen before.
//   2. `wizzyBaselineSearchResponse` — the unfiltered page-1 result,
//      reused when the user clears every filter back to that state.
//   3. `window.__wizzyData` (head prefetch) — reused when clearing
//      filters lands back on the exact query the <head> script already
//      fetched before this file ran.
// Separately, `wizzyInFlightProductRequests`/`wizzyInFlightAutocomplete`
// de-dupe concurrent identical requests (e.g. double-clicking a filter).

/** In-flight requests keyed by full request body to avoid duplicate API calls. */
const wizzyInFlightProductRequests = new Map();

/** Search + filter responses keyed by `apiMode:requestKey` (session LRU). */
const WIZZY_PRODUCT_RESPONSE_CACHE_MAX = 50;
const wizzyProductResponseCache = new Map();

/** Autocomplete responses keyed by normalized query (session memory). */
const WIZZY_AUTOCOMPLETE_CACHE_MAX = 30;
const wizzyAutocompleteCache = new Map();
const wizzyInFlightAutocomplete = new Map();

/** Aborts stale modal autocomplete requests when the user keeps typing. */
let wizzyAutocompleteAbortController = null;

function bumpAutocompleteStale() {
  wizzyAutocompleteGeneration++;
}

function wizzyNormalizeQuery(query) {
  return String(query || "").trim();
}

/** Cache key for autocomplete — lowercase trimmed query. */
function wizzyAutocompleteCacheKey(query) {
  return wizzyNormalizeQuery(query).toLowerCase();
}

function wizzyRememberAutocomplete(query, response) {
  const key = wizzyAutocompleteCacheKey(query);
  if (!key || !response) return;
  if (wizzyAutocompleteCache.size >= WIZZY_AUTOCOMPLETE_CACHE_MAX) {
    const oldest = wizzyAutocompleteCache.keys().next().value;
    wizzyAutocompleteCache.delete(oldest);
  }
  wizzyAutocompleteCache.set(key, response);
}

function wizzySearchStateCacheId(filtersKey) {
  let h = 5381;
  for (let i = 0; i < filtersKey.length; i++) {
    h = (h * 33) ^ filtersKey.charCodeAt(i);
  }
  return `fk_${(h >>> 0).toString(36)}`;
}

/** Logs q / page / sort for cache debugging (request body must match URL state). */
function wizzySearchStateSummary(requestKey) {
  try {
    const body = JSON.parse(requestKey);
    let sortField;
    let sortOrder;
    if (typeof body.sort === "string") {
      const parsedSort = JSON.parse(body.sort);
      sortField = parsedSort?.[0]?.field;
      sortOrder = parsedSort?.[0]?.order;
    } else if (Array.isArray(body.sort)) {
      sortField = body.sort[0]?.field;
      sortOrder = body.sort[0]?.order;
    }
    return {
      q: body.q,
      page: body.page,
      sortField,
      sortOrder,
      attributeKeys: body.attributes ? Object.keys(body.attributes) : [],
      facetKeys: ["sizes", "colors"].filter((k) => Array.isArray(body[k]) && body[k].length),
    };
  } catch (_) {
    return { parseError: true };
  }
}

function wizzyLogCache(event, requestKey, extra) {
  if (!WIZZY_CACHE_DEBUG) return;
  console.log(`[wizzy-cache] ${event}`, {
    at: Date.now(),
    cacheId: requestKey ? wizzySearchStateCacheId(requestKey) : null,
    state: requestKey ? wizzySearchStateSummary(requestKey) : null,
    url: window.location.search,
    ...(extra || {}),
  });
}

/**
 * Composite cache key — search and filter bodies can differ for the same UI state.
 */
function wizzyProductCacheCompositeKey(requestKey, apiMode) {
  return `${apiMode}:${requestKey}`;
}

/**
 */
function wizzyGetCachedProductResponse(requestKey, apiMode) {
  if (!requestKey || !apiMode) return null;
  return wizzyProductResponseCache.get(wizzyProductCacheCompositeKey(requestKey, apiMode)) ?? null;
}

/**
 * Stores a product response in the session LRU cache.
 */
function wizzyRememberProductResponse(requestKey, apiMode, response) {
  if (!requestKey || !apiMode || !response) return;
  const composite = wizzyProductCacheCompositeKey(requestKey, apiMode);
  if (wizzyProductResponseCache.has(composite)) {
    wizzyProductResponseCache.delete(composite);
  } else if (wizzyProductResponseCache.size >= WIZZY_PRODUCT_RESPONSE_CACHE_MAX) {
    const oldest = wizzyProductResponseCache.keys().next().value;
    wizzyProductResponseCache.delete(oldest);
  }
  wizzyProductResponseCache.set(composite, response);
}

/**
 * Applies a cached search/filter response when available.
 */
function wizzyTryApplyCachedProductResponse(requestKey, apiMode, autocompleteGen) {
  const cached = wizzyGetCachedProductResponse(requestKey, apiMode);
  if (!cached) return false;
  wizzyLogCache("HIT → session cache", requestKey, {
    apiMode,
    cacheSize: wizzyProductResponseCache.size,
  });
  wizzyRememberFilterState(requestKey, cached, "cache", apiMode);
  applyWizzyFilterResponse(cached, autocompleteGen, "cache");
  return true;
}

/** True when the price slider differs from the facet min/max (user applied a price filter). */
function wizzyIsPriceFilterActive() {
  const { min, max, currentMin, currentMax } = wizzyPriceState;
  if (min == null || max == null) return false;
  return Number(currentMin) !== Number(min) || Number(currentMax) !== Number(max);
}

/** Resolves whether sellingPrice must be sent on the next filter API call. */
function wizzyResolvePriceFilterActive(isPriceArg) {
  if (isPriceArg === true) return true;
  if (isPriceArg === false) return false;
  return wizzyIsPriceFilterActive();
}

function wizzyStoreBaselineSearchIfNeeded(requestKey, apiMode, response) {
  if (apiMode !== "search" || page.pageCount !== 1 || !response?.payload) return;
  wizzyBaselineSearchKey = requestKey;
  wizzyBaselineSearchResponse = response;
}

function wizzyTryApplyBaselineSearch(requestKey, apiMode, autocompleteGen) {
  if (apiMode !== "search" || page.pageCount !== 1 || !wizzyBaselineSearchResponse) return false;
  if (requestKey !== wizzyBaselineSearchKey) return false;
  wizzyLogCache("HIT → baseline unfiltered search", requestKey, { apiMode });
  wizzyRememberFilterState(requestKey, wizzyBaselineSearchResponse, "baseline", apiMode);
  applyWizzyFilterResponse(wizzyBaselineSearchResponse, autocompleteGen, "baseline");
  return true;
}

/** Head prefetch / __wizzyData — used when clearing filters back to query-only search. */
function wizzyTryApplyPrefetchSearch(requestKey, apiMode, autocompleteGen) {
  if (apiMode !== "search" || page.pageCount !== 1) return false;
  const wd = window.__wizzyData;
  if (!wd?.data?.payload) return false;
  if (wizzyNormalizeQuery(wd.query) !== wizzyNormalizeQuery(searchQuery)) return false;
  const expectedKey = JSON.stringify(buildWizzySearchBody(searchQuery, 1));
  if (requestKey !== expectedKey) return false;
  wizzyLogCache("HIT → __wizzyData prefetch (filters cleared)", requestKey, {
    apiMode,
  });
  wizzyRememberFilterState(requestKey, wd.data, "__wizzyData-restore", apiMode);
  applyWizzyFilterResponse(wd.data, autocompleteGen, "__wizzyData-restore");
  return true;
}

function wizzyTryApplyStoredProductResponse(requestKey, apiMode, autocompleteGen) {
  if (wizzyTryApplyCachedProductResponse(requestKey, apiMode, autocompleteGen)) return true;
  if (wizzyTryApplyBaselineSearch(requestKey, apiMode, autocompleteGen)) return true;
  if (wizzyTryApplyPrefetchSearch(requestKey, apiMode, autocompleteGen)) return true;
  return false;
}

function wizzyResetFilterUiState() {
  document.querySelectorAll(".neo-filters-item.active").forEach((el) => {
    el.classList.remove("active");
  });
  document.querySelectorAll(".neo-filters-accordion-title-text-count").forEach((el) => {
    el.textContent = "";
  });
  const priceEl = wizzyDom.priceRange;
  if (priceEl) {
    priceEl.setAttribute("min-value", wizzyPriceState.currentMin);
    priceEl.setAttribute("max-value", wizzyPriceState.currentMax);
  }
}

function wizzyRememberFilterState(requestKey, response, source, apiMode) {
  if (!response || !requestKey) return;
  wizzyActiveFilterState = {
    filtersKey: requestKey,
    response,
    apiMode: apiMode || null,
  };
  if (apiMode) wizzyRememberProductResponse(requestKey, apiMode, response);
  wizzyStoreBaselineSearchIfNeeded(requestKey, apiMode, response);
  wizzyLogCache("SAVED → cache", requestKey, {
    source: source || "unknown",
    apiMode: apiMode || null,
    productCount: response?.payload?.result?.length ?? 0,
    total: response?.payload?.total,
    cacheSize: wizzyProductResponseCache.size,
  });
}

/** True when product cards were already rendered for this page load. */
function wizzySearchPageAlreadyHydrated() {
  return !!(window.__wizzySearchHydrateOnce && wizzyActiveFilterState.response != null);
}

function wizzyMarkSearchPageHydrated() {
  window.__wizzySearchHydrateOnce = true;
  if (window.__wizzyData) window.__wizzyData.consumed = true;
}

/* ==========================================================================
      Utilities (debounce, escapeHtml)
      ========================================================================== */

/**
 * Creates a debounced wrapper to limit rapid function calls.
 */
function debounce(fn, delay = 650) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Escapes HTML special characters for safe template output.
 */
function escapeHtml(str) {
  return String(str ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/* ==========================================================================
      DOM helpers
      ========================================================================== */

/**
 * Queries static Wizzy search DOM once; reuse wizzyDom refs everywhere else.
 * Modal refs are all scoped under `.neo-search-container` — see
 * `snippets/neo-search-modal.liquid`. Two near-identical inputs exist (desktop
 * pill + mobile sheet header), switched by a CSS breakpoint, so each is
 * queried within its own ancestor to avoid picking the wrong one.
 */
function initWizzyDomCache() {
  const neoRoot = document.querySelector(".neo-search-container");
  wizzyDom.neoSearchContainer = neoRoot;
  wizzyDom.neoOpenButton = neoRoot?.querySelector(".neo-search-button") ?? null;
  wizzyDom.neoDesktopInput = neoRoot?.querySelector(".neo-search-desktop-container .neo-search-desktop-input") ?? null;
  wizzyDom.neoDesktopSubmitBtn = neoRoot?.querySelector(".neo-search-desktop-submit-search-button") ?? null;
  wizzyDom.neoDesktopCloseBtn = neoRoot?.querySelector(".neo-search-desktop-close") ?? null;
  wizzyDom.searchModal = neoRoot?.querySelector(".neo-search-modal-container") ?? null;
  wizzyDom.neoModalOverlay = neoRoot?.querySelector(".neo-search-modal-overlay") ?? null;
  wizzyDom.neoMobileInput = neoRoot?.querySelector(".neo-search-mobile-form-conatiner .neo-search-mobile-input") ?? null;
  wizzyDom.neoMobileSubmitBtn = neoRoot?.querySelector('.neo-search-mobile-form-conatiner button[type="submit"]') ?? null;
  wizzyDom.neoMobileCloseBtn = neoRoot?.querySelector(".neo-search-mobile-close") ?? null;
  wizzyDom.modalLeftPanel = neoRoot?.querySelector(".neo-search-left") ?? null;
  wizzyDom.modalRightPanel = neoRoot?.querySelector(".neo-search-right") ?? null;
  wizzyDom.neoProductContainer = neoRoot?.querySelector(".neo-search-product-container") ?? null;
  wizzyDom.recentList = neoRoot?.querySelector(".neo-search-recent-search-list") ?? null;
  wizzyDom.recentContainer = neoRoot?.querySelector(".neo-search-recent-search-container") ?? null;
  wizzyDom.trendingUl = neoRoot?.querySelector(".neo-search-trending-search-list") ?? null;
  wizzyDom.searchProductGrid = document.querySelector(".search-product-grid");
  wizzyDom.searchRightPanel = wizzyDom.searchProductGrid?.querySelector(".right-panel") ?? null;
  wizzyDom.searchProductContainer = wizzyDom.searchProductGrid?.querySelector("#productContainer") ?? null;
  wizzyDom.searchProductGridContainer = wizzyDom.searchRightPanel?.querySelector(".product-container") ?? null;
  wizzyDom.filtersContainer = document.querySelector(".neo-filters-container");
  wizzyDom.productCountContainer = document.querySelector(".neo-product-count");
  wizzyDom.clearAllBtn = document.querySelector(".neo-filter-header-clear-all");
  wizzyDom.priceRange = document.querySelector("neo-price-range");
  wizzyDom.filterAside = document.querySelector(".left-panel.neo-collection-aside");
  wizzyDom.mobileSortOverlay = document.getElementById("mobile-sort-overlay");
  wizzyDom.mobileSortDrawer = document.getElementById("mobile-sort-drawer");
  wizzyDom.searchPrefetchContainer = document.querySelector('#productContainer.product-container.neo-product-grid[data-wizzy-product-container="true"]');
}

/** Autocomplete results live here; clearing input programmatically does not fire `input`. */
function clearWizzyModalProductContainer() {
  bumpAutocompleteStale();
  if (wizzyDom.neoProductContainer) wizzyDom.neoProductContainer.innerHTML = "";
}

/**
 * Trending search list — items are real `<a href="/search?q=...">` anchors,
 * so no click handler is needed for navigation. Capped to
 * `WIZZY_TRENDING_SEARCH_LIMIT`; built off-DOM then swapped in one write.
 */
function createTrendingList(trnedingList) {
  const ul = wizzyDom.trendingUl;
  if (!ul) return;

  const terms = (trnedingList || []).slice(0, WIZZY_TRENDING_SEARCH_LIMIT);
  const frag = document.createDocumentFragment();
  terms.forEach((trend) => {
    const li = document.createElement("li");
    li.className = "neo-search-trending-search-list-item";
    li.innerHTML = `<a href="/search?q=${encodeURIComponent(trend)}">${escapeHtml(trend)}</a>`;
    frag.appendChild(li);
  });

  requestAnimationFrame(() => {
    ul.innerHTML = "";
    ul.appendChild(frag);
  });
}

/** Restores the static trending list fetched from Wizzy trendingSearches API. */
function resetModalTrendingList() {
  createTrendingList(trendingList || []);
}

/** Same accessor pattern for all three: pick the modal's DOM node while the user is
 *  typing (`modalRightPanel === "input"`), otherwise the /search page's node. This
 *  lets `renderAutocompleteProducts()` and friends stay agnostic to which surface
 *  triggered them. */
function getProductContainer() {
  return modalRightPanel === "input" ? wizzyDom.neoProductContainer : wizzyDom.searchProductContainer;
}

function getRightPanel() {
  return modalRightPanel === "input" ? wizzyDom.modalRightPanel : wizzyDom.searchRightPanel;
}

function getProductGridContainer() {
  return modalRightPanel === "input" ? wizzyDom.neoProductContainer : wizzyDom.searchProductGridContainer;
}

function getScrollSkeletonTrigger() {
  const root = modalRightPanel === "input" ? wizzyDom.searchModal : wizzyDom.searchProductGrid;
  return root?.querySelector(".grid__item--skeleton") ?? null;
}

function getActiveFilterValues() {
  const activeValues = [];
  Object.values(wizzyAttributes).forEach((list) => activeValues.push(...list));
  Object.values(arraysObj).forEach((list) => activeValues.push(...list));
  return activeValues;
}

function markActiveFilterElements(filterElements, activeValues) {
  filterElements.forEach((el) => {
    const key = el.getAttribute("data-filter-key");
    if (key && activeValues.includes(key)) {
      el.classList.add("active");
    }
  });

  const colorGroups = new Set();
  filterElements.forEach((el) => {
    if (!el.classList.contains("active")) return;
    const colorCheckbox = el.querySelector(".neo-filters-color-checkbox");
    if (!colorCheckbox) return;
    const parent = colorCheckbox.parentNode?.parentNode;
    if (parent) colorGroups.add(parent);
  });

  colorGroups.forEach((group) => activateColorSvg(group));
}

function initFilterDelegation() {
  // Collection pages use neo-collection.js for filter accordions; binding here
  // races with initFilterAccordions() and prevents panels from staying open.
  if (!isSearchPage()) return;

  const filtersContainer = wizzyDom.filtersContainer;
  if (!filtersContainer || filtersContainer.dataset.wizzyFilterDelegationBound === "true") return;
  filtersContainer.dataset.wizzyFilterDelegationBound = "true";

  filtersContainer.addEventListener("click", (event) => {
    const accordionTitle = event.target.closest(".neo-filters-accordion-title");
    if (accordionTitle && filtersContainer.contains(accordionTitle)) {
      openAccordion({ currentTarget: accordionTitle, target: event.target });
      return;
    }

    const filterItem = event.target.closest(".neo-filters-item");
    if (filterItem && filtersContainer.contains(filterItem)) {
      createFilterRequest(event);
    }
  });
}

function initRecentListDelegation() {
  const recentList = wizzyDom.recentList;
  if (!recentList || recentList.dataset.wizzyRecentDelegationBound === "true") return;
  recentList.dataset.wizzyRecentDelegationBound = "true";

  recentList.addEventListener("click", (event) => {
    const removeBtn = event.target.closest('[data-action="remove-recent"]');
    if (removeBtn) {
      handleRemoveItem(event);
    }
  });
}

/**
 * Bound to the `input` event on whichever of the two search inputs applies
 * (see `bindNeoSearchInputs()`). Debounced by `WIZZY_AUTOCOMPLETE_DEBOUNCE_MS`
 * so autocomplete only fires once typing pauses. Flow: mark the modal as
 * "input" mode → reveal the results view once there's a query → hand the
 * query to `getInputValue()`, which routes to `sendAutocompleteRequest()`.
 * Opening the modal itself is `openNeoSearch()`'s job, not this handler's —
 * by the time an input can receive keystrokes, `.active` is already set.
 */
function createAutocompleteInputHandler(getInputEl) {
  return debounce(async () => {
    const input = getInputEl();
    if (!input) return;

    const q = input.value?.trim() || "";
    searchQuery = q;
    modalRightPanel = "input";

    updateSearchModalState(q);

    if (!q) {
      clearWizzyModalProductContainer();
      return;
    }

    await getInputValue(q);
  }, WIZZY_AUTOCOMPLETE_DEBOUNCE_MS);
}

/* ==========================================================================
      Modal
========================================================================== */
      // Header search modal (snippets/neo-search-modal.liquid). Two class
      // toggles drive the entire UI, both pure CSS transitions with no
      // JS-computed inline styles or layout reads:
      //   - `.active` on `.neo-search-container` — open/close everything
      //     (expands the desktop pill, fades in its icons, reveals the modal).
      //   - `.full-width` on `.neo-search-modal-container` — reveals the
      //     "results" view (dark overlay, right panel/product grid) once
      //     there's a query; see `updateSearchModalState()`.
      // `.neo-search-button` (open) and the two close buttons are separate,
      // visually-overlapping elements (not one button with a swapped icon),
      // so open/close are two small dedicated functions rather than one
      // stateful toggle. Per the liquid file's own comments: commit the
      // class change synchronously first, defer clearing/bookkeeping into
      // `requestIdleCallback` (close) or `setTimeout(0)` (submit/redirect) —
      // and never a `form submit` listener (GTM tracks it and blocks the
      // main thread); use `click` + `keydown` (Enter) only.

let searchModalAutoPlayPaused = false;
let searchModalAutoPlayResumeState = {};

/**
 * Stops homepage Swiper autoplay while search is open to cut INP/input delay.
 */
function pauseAutoPlayingSwipersForSearch() {
  if (searchModalAutoPlayPaused) return;

  searchModalAutoPlayPaused = true;
  searchModalAutoPlayResumeState = {};

  const swipers = window.autoPlayingSwiper || {};
  for (const [id, swiper] of Object.entries(swipers)) {
    if (swiper?.autoplay?.running) {
      searchModalAutoPlayResumeState[id] = true;
      swiper.autoplay.stop();
    }
  }
}

/**
 * Restores Swiper autoplay that was running when search opened.
 */
function resumeAutoPlayingSwipersAfterSearch() {
  if (!searchModalAutoPlayPaused) return;

  searchModalAutoPlayPaused = false;

  const swipers = window.autoPlayingSwiper || {};
  for (const id of Object.keys(searchModalAutoPlayResumeState)) {
    const swiper = swipers[id];
    if (typeof swiper?.autoplay?.start === "function") {
      swiper.autoplay.start();
    }
  }

  searchModalAutoPlayResumeState = {};
}

/**
 * Reveals the "results" state (dark overlay, right panel, product grid) once
 * there's a query — a single class toggle. Replaces the old design's
 * `getBoundingClientRect()` read + large inline `style.cssText` write on
 * every keystroke, which was both a forced reflow and an oversized style
 * recalculation.
 */
function updateSearchModalState(query) {
  requestAnimationFrame(() => {
    wizzyDom.searchModal?.classList.toggle("full-width", !!query);
  })
}

/**
 * Lazily binds the debounced autocomplete `input` handler and Enter-to-submit
 * to whichever input applies for the current breakpoint — decided once via
 * the already-cached `window.innerWidthCached` (never a live layout read),
 * per the liquid file's explicit "only add listeners to this input if
 * width..." comments. Idempotent via a dataset flag; called from
 * `openNeoSearch()` on first open only.
 */
function bindNeoSearchInputs() {
  const isDesktop = window.innerWidthCached > 1024;
  const input = isDesktop ? wizzyDom.neoDesktopInput : wizzyDom.neoMobileInput;
  if (!input || input.dataset.wizzyAutocompleteInputBound === "true") return;
  input.dataset.wizzyAutocompleteInputBound = "true";

  const handler = createAutocompleteInputHandler(() => input);
  input.addEventListener("input", handler);

  input.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    submitNeoSearch(input);
  });
}
window.addEventListener('DOMContentLoaded', bindNeoSearchInputs, {once: true})

/**
 * Click handler for `.neo-search-button` — always "open" (there is no
 * separate close state to toggle back from; the close buttons are their own
 * elements). Adds `.active` immediately, a single scoped class write that is
 * all CSS needs to animate the whole UI open, then defers autoplay-pause,
 * input binding, and trending/recent list rendering past this paint.
 */
function openNeoSearch(event) {
  event?.stopPropagation();
  const container = wizzyDom.neoSearchContainer;
  if (!container) return;

  container.classList.add("active");
  modalRightPanel = "input";
  document.body.style.overflow = "hidden";

  setTimeout(() => {
    pauseAutoPlayingSwipersForSearch();
  }, 0);

  const input = window.innerWidthCached > 1024 ? wizzyDom.neoDesktopInput : wizzyDom.neoMobileInput;
  setTimeout(() => {
    input?.focus({ preventScroll: true });
  }, 5);

}

renderRecentSearches();

/**
 * Closes the modal: removes `.active`/`.full-width` synchronously first —
 * immediate paint, matches the liquid file's explicit "remove active class...
 * remove full-width class" instructions — then clears input values and
 * rendered results inside `requestIdleCallback` so non-visual cleanup never
 * delays the close animation.
 */
function closeNeoSearch() {
  const container = wizzyDom.neoSearchContainer;
  if (!container) return;

  container.classList.remove("active");
  wizzyDom.searchModal?.classList.remove("full-width");
  document.body.style.overflow = null;
  resumeAutoPlayingSwipersAfterSearch();

  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 100));
  idle(
    () => {
      if (wizzyDom.neoDesktopInput) wizzyDom.neoDesktopInput.value = "";
      if (wizzyDom.neoMobileInput) wizzyDom.neoMobileInput.value = "";
      clearWizzyModalProductContainer();

      if (window.location.pathname.includes("/search")) {
        const params = new URLSearchParams(window.location.search);
        searchQuery = params.get("q") || "";
        modalRightPanel = "search";
      }
    },
    { timeout: 100 },
  );
}

/**
 * Two-phase submit, per the liquid file's explicit instructions: close the
 * modal first (visual commit, before any redirect), then — in a following
 * task — persist the query to recent history and navigate. Bound to both
 * submit buttons' click and both inputs' Enter keydown; never a `form
 * submit` listener.
 */
function submitNeoSearch(input) {
  const q = (input?.value || "").trim();
  if (!q) return;
  closeNeoSearch();
  setTimeout(() => {
    storeSearchQueryParams({ q });
    redirectToSearchPage({ q });
  }, 0);
}

/**
 * Closes header search when open (e.g. ATC from trending products).
 * @returns {boolean} Whether the search UI was open and got closed.
 */
function wizzyCloseSearchModalIfOpen() {
  initWizzyDomCache();

  const isOpen = wizzyDom.neoSearchContainer?.classList.contains("active");
  if (!isOpen) return false;

  closeNeoSearch();
  return true;
}

/** Delegates to `closeNeoSearch()` — bound to the modal overlay click. */
function clickOverlay() {
  closeNeoSearch();
}

/**
 * One-time wiring for the header search modal: open button, both close
 * buttons, both submit buttons, the overlay, and an outside-click listener.
 * Input-specific listeners (typing/Enter) are bound lazily on first open via
 * `bindNeoSearchInputs()`, not here.
 */
function initNeoSearchModal() {
  const container = wizzyDom.neoSearchContainer;
  if (!container || container.dataset.wizzyModalBound === "true") return;
  container.dataset.wizzyModalBound = "true";

  wizzyDom.neoOpenButton?.addEventListener("click", openNeoSearch);

  wizzyDom.neoDesktopCloseBtn?.addEventListener("click", (event) => {
    event.preventDefault();
    closeNeoSearch();
  });
  wizzyDom.neoMobileCloseBtn?.addEventListener("click", (event) => {
    event.preventDefault();
    closeNeoSearch();
  });

  wizzyDom.neoDesktopSubmitBtn?.addEventListener("click", (event) => {
    event.preventDefault();
    submitNeoSearch(wizzyDom.neoDesktopInput);
  });
  wizzyDom.neoMobileSubmitBtn?.addEventListener("click", (event) => {
    event.preventDefault();
    submitNeoSearch(wizzyDom.neoMobileInput);
  });

  wizzyDom.neoModalOverlay?.addEventListener("click", closeNeoSearch);

  document.addEventListener("click", (event) => {
    if (!container.classList.contains("active")) return;
    if (container.contains(event.target)) return;
    closeNeoSearch();
  });
}

/* ==========================================================================
      Search history & trending
      ========================================================================== */

function storeSearchQueryParams(params) {
  const current = getSearchQueryParamsFromSessionStorage();
  const normalized = {
    q: String(params?.q ?? "").trim(),
  };
  if (!normalized.q) return;

  const filtered = current.filter((item) => String(item?.q ?? "").trim() !== normalized.q);
  filtered.unshift(normalized);
  sessionStorage.setItem(WIZZY_SEARCH_HISTORY_KEY, JSON.stringify(filtered));
  renderRecentSearches();
}

function getSearchQueryParamsFromSessionStorage() {
  try {
    const raw = sessionStorage.getItem(WIZZY_SEARCH_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

/** Max recent-search entries rendered — per snippets/neo-search-modal.liquid comments. */
const WIZZY_RECENT_SEARCH_LIMIT = 5;

/**
 * Recent search list — items are real `<a href="/search?q=...">` anchors
 * (native navigation, no JS click handler needed) with a small per-item
 * "remove" button. Capped to `WIZZY_RECENT_SEARCH_LIMIT`.
 */
function renderRecentSearches() {
  const recentList = wizzyDom.recentList;
  const recentHeader = wizzyDom.recentContainer;

  if (!recentList) return;

  const recentItems = getSearchQueryParamsFromSessionStorage().slice(0, WIZZY_RECENT_SEARCH_LIMIT);

  if (!recentItems.length) {
    // Batch the clear + header toggle into one mutation, deferred to just
    // before paint, instead of writing them separately right away.
    requestAnimationFrame(() => {
      recentList.innerHTML = "";
      if (recentHeader) recentHeader.style.display = "none";
    });
    return;
  }

  const frag = document.createDocumentFragment();
  recentItems.forEach((item, index) => {
    const q = String(item?.q ?? "").trim();
    if (!q) return;

    const li = document.createElement("li");
    li.className = "neo-search-recent-search-list-item";
    li.setAttribute("data-recent-index", String(index));
    li.innerHTML = `
                  <a href="/search?q=${encodeURIComponent(q)}">${escapeHtml(q)}</a>
                  <button class="neo-search-recent-remove-btn" type="button" data-action="remove-recent">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5.293 5.293a1 1 0 0 1 1.414 0L12 10.586l5.293-5.293a1 1 0 1 1 1.414 1.414L13.414 12l5.293 5.293a1 1 0 0 1-1.414 1.414L12 13.414l-5.293 5.293a1 1 0 0 1-1.414-1.414L10.586 12 5.293 6.707a1 1 0 0 1 0-1.414" fill="rgba(255, 255, 255, 0.3)"/>
                      </svg>
                  </button>
              `;
    frag.appendChild(li);
  });

  // Build off-DOM above, then swap it in as a single batched mutation.
  requestAnimationFrame(() => {
    recentList.innerHTML = "";
    recentList.appendChild(frag);
    if (recentHeader) recentHeader.style.display = "flex";
  });
}

function redirectToSearchPage(params) {
  const searchUrl = new URL("/search", window.location.origin);
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    searchUrl.searchParams.set(key, String(value));
  });
  window.location.href = searchUrl.toString();
}

/**
 * Removes one recent search item.
 */
function handleRemoveItem(event) {
  const removeBtn = event.target.closest('[data-action="remove-recent"]');
  if (!removeBtn) return;

  event.preventDefault();
  event.stopPropagation();

  const itemEl = removeBtn.closest(".neo-search-recent-search-list-item");
  const idx = Number(itemEl?.getAttribute("data-recent-index"));
  if (Number.isNaN(idx)) return;

  const recentItems = getSearchQueryParamsFromSessionStorage();
  recentItems.splice(idx, 1);
  sessionStorage.setItem(WIZZY_SEARCH_HISTORY_KEY, JSON.stringify(recentItems));
  renderRecentSearches();
}

/**
 * Clears the full recent search list.
 */
function handleClearAll(event) {
  const recentList = wizzyDom.recentList;
  const recentHeader = wizzyDom.recentContainer;
  if (!recentList || !recentHeader) return;

  recentHeader.style.display = "none";
  recentList.innerHTML = "";
  sessionStorage.removeItem(WIZZY_SEARCH_HISTORY_KEY);
}

async function fetchTrendingSearches() {
  if (wizzyTrendingCache) return wizzyTrendingCache;
  if (wizzyTrendingInFlight) return wizzyTrendingInFlight;

  wizzyTrendingInFlight = (async () => {
    const response = await fetch(`${WIZZY_API_BASE}/trendingSearches`, {
      method: "GET",
      headers: getWizzyApiHeaders(),
    });

    if (!response.ok) return [];

    const res = await response.json();
    const queries = res?.payload?.queries || [];
    wizzyTrendingCache = queries;
    return queries;
  })();

  try {
    return await wizzyTrendingInFlight;
  } finally {
    wizzyTrendingInFlight = null;
  }
}

/** Max trending-search entries rendered — per snippets/neo-search-modal.liquid comments. */
const WIZZY_TRENDING_SEARCH_LIMIT = 10;

/* ==========================================================================
      API & filter requests
      ========================================================================== */
      // This is the heart of the file. Two request-building/dispatch paths:
      //   - Modal typing: getInputValue() → sendAutocompleteRequest() → hits
      //     /v1/autocomplete → applyWizzyAutocompleteResponse() renders a
      //     small preview list (no filters, no pagination, no URL sync).
      //   - /search page (filters, sort, pagination, or a plain query): every
      //     entry point (filter click, sort change, price slider, infinite
      //     scroll, page-load bootstrap) calls createFilterSearchQuery(),
      //     which is the single funnel that: picks search-vs-filter endpoint
      //     → checks the cache waterfall → fetches on a miss → hydrates via
      //     applyWizzyFilterResponse().
      // `autocompleteGen` is threaded through both paths so a response that
      // resolves after the user has since cleared/changed the query is
      // silently dropped instead of overwriting newer UI state.

/**
 * Reads the query and triggers product search with loading skeletons.
 * Called from the debounced input handler and from filter/sort actions that
 * need to re-run the current query. Routes to autocomplete while the modal
 * is in "input" mode, otherwise to the full filter/search flow.
 */
async function getInputValue(queryOverride = null) {
  const inputQuery = (queryOverride ?? "").toString().trim();
  if (!inputQuery) return;

  updateSearchModalState(inputQuery);
  if (searchQuery !== inputQuery) wizzySearchedKey = "";
  searchQuery = inputQuery;
  const autocompleteGen = ++wizzyAutocompleteGeneration;

  if (modalRightPanel === "input") {
    await sendAutocompleteRequest(inputQuery, autocompleteGen);
    return;
  }

  await createFilterSearchQuery(wizzyAttributes, arraysObj, false, autocompleteGen);
}

/**
 * Builds the Wizzy autocomplete request body for modal typing.
 */
function buildAutocompleteBody(query) {
  return {
    ...WIZZY_AUTOCOMPLETE_DEFAULTS,
    q: query,
    // Wizzy autocomplete expects sections as a JSON-encoded string (not a native array).
    sections: JSON.stringify([{ key: "others" }, { key: "categories" }]),
  };
}

/**
 * Shared Wizzy API headers (store id + api key from env.liquid).
 */
function getWizzyApiHeaders() {
  return {
    Accept: "application/json, text/plain, */*",
    "Content-Type": "application/json",
    "x-store-id": window.wizzyStoreId,
    "x-api-key": window.wizzyApiKey,
  };
}

/**
 * Normalizes product array from autocomplete API response.
 */
function extractAutocompleteProducts(response) {
  const products = response?.payload?.products;
  if (Array.isArray(products?.result)) return products.result;
  if (Array.isArray(products)) return products;
  if (Array.isArray(response?.payload?.result)) return response.payload.result;
  if (Array.isArray(response?.products)) return response.products;
  return [];
}

/**
 * Normalizes suggestion labels from autocomplete sections (others, categories, brands).
 */
function extractAutocompleteSuggestionLabels(response) {
  const payload = response?.payload || {};
  const seen = new Set();
  const labels = [];

  const appendLabel = (item) => {
    const label = item?.value || item?.name;
    if (!label || seen.has(label)) return;
    seen.add(label);
    labels.push(label);
  };

  (payload.others || []).forEach(appendLabel);
  (payload.categories || []).forEach(appendLabel);
  (payload.brands || []).forEach(appendLabel);

  return labels;
}

/**
 * Fetches autocomplete products for modal typing (not filter API).
 * Order of checks, cheapest first: memory cache → an already-in-flight
 * request for the same normalized query → a fresh network call. Any
 * previous in-flight request for a *different* query is aborted first so a
 * fast typist never has two autocomplete responses racing each other.
 */
async function sendAutocompleteRequest(query, autocompleteGen) {
  const normalized = wizzyNormalizeQuery(query);

  if (!normalized) {
    clearWizzyModalProductContainer();
    return;
  }

  const cacheKey = wizzyAutocompleteCacheKey(normalized);
  const cached = wizzyAutocompleteCache.get(cacheKey);
  if (cached) {
    wizzyLogCache("autocomplete HIT → memory", cacheKey, { q: normalized });
    applyWizzyAutocompleteResponse(cached, autocompleteGen);
    return;
  }

  const inFlight = wizzyInFlightAutocomplete.get(cacheKey);
  if (inFlight) {
    wizzyLogCache("autocomplete AWAIT in-flight", cacheKey, { q: normalized });
    try {
      const response = await inFlight;
      if (response) applyWizzyAutocompleteResponse(response, autocompleteGen);
    } catch (error) {
      if (error?.name !== "AbortError") console.error("[wizzy-autocomplete] in-flight error", error);
    }
    return;
  }

  if (wizzyAutocompleteAbortController) {
    wizzyAutocompleteAbortController.abort();
  }
  wizzyAutocompleteAbortController = new AbortController();

  createSkeletonLoaders();

  const task = (async () => {
    const res = await fetch(`${WIZZY_API_BASE}/autocomplete`, {
      method: "POST",
      headers: getWizzyApiHeaders(),
      body: JSON.stringify(buildAutocompleteBody(normalized)),
      signal: wizzyAutocompleteAbortController.signal,
    });

    if (!res.ok) {
      throw new Error(`Wizzy autocomplete HTTP ${res.status}`);
    }

    const response = await res.json();
    wizzyRememberAutocomplete(normalized, response);
    return response;
  })();

  wizzyInFlightAutocomplete.set(cacheKey, task);

  try {
    const response = await task;
    applyWizzyAutocompleteResponse(response, autocompleteGen);
  } catch (error) {
    if (error?.name === "AbortError") return;
    console.error("[wizzy-autocomplete] network error", error);
  } finally {
    wizzyInFlightAutocomplete.delete(cacheKey);
  }
}

/**
 * Renders modal autocomplete products (no filters / infinite scroll).
 */
function applyWizzyAutocompleteResponse(response, autocompleteGen) {
  if (autocompleteGen != null && autocompleteGen !== wizzyAutocompleteGeneration) return;

  const suggestions = extractAutocompleteSuggestionLabels(response);
  if (suggestions.length) {
    createTrendingList(suggestions);
  } else {
    resetModalTrendingList();
  }

  const products = extractAutocompleteProducts(response);
  renderAutocompleteProducts(products);
}

function createSkeletonLoaders() {
  const productContainer = getProductContainer();
  if (!productContainer) return;

  const shouldClear = modalRightPanel === "input";

  const productFragment = document.createDocumentFragment();
  for (let i = 1; i <= 5; i++) {
    const skel = document.createElement("div");
    skel.classList.add("grid__item", "grid__item--skeleton");
    skel.innerHTML = `<skeleton style="${WIZZY_SKELETON_STYLE}"></skeleton>`;
    productFragment.appendChild(skel);
  }

  // Batch the clear + insert into a single mutation right before paint,
  // instead of clearing and appending as two separate writes.
  requestAnimationFrame(() => {
    if (shouldClear) productContainer.innerHTML = "";
    productContainer.appendChild(productFragment);
  });
}

/**
 * Mirrors current filter/sort/price/query state into the URL (via
 * `history.replaceState`, no reload) so refreshing or sharing the link
 * reproduces the same results — restored on load by `applyRefreshFilters()`.
 * Strips every Wizzy-owned param first, then re-adds only what's active.
 */
function addQueryInUrl(wizzyAttributes, arraysObj, isPrice) {
  const currentUrl = new URL(window.location.href);
  const params = currentUrl.searchParams;
  const useFilterApi = wizzyShouldUseFilterApi(wizzyAttributes, arraysObj, isPrice);

  params.delete("q");
  params.delete("page");
  params.delete("sortField");
  params.delete("sortOrder");
  params.delete("priceMin");
  params.delete("priceMax");
  params.delete(WIZZY_SEARCHED_KEY_PARAM);

  const keysToDelete = [];
  params.forEach((_, key) => {
    if (key.startsWith("attr.") || key.startsWith("facet.")) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((key) => params.delete(key));

  if (!searchQuery) {
    window.history.replaceState({}, "", currentUrl.pathname);
    return;
  }

  params.set("q", searchQuery);
  if (wizzySearchedKey) params.set(WIZZY_SEARCHED_KEY_PARAM, wizzySearchedKey);

  if (!useFilterApi) {
    window.history.replaceState({}, "", `${currentUrl.pathname}?${params.toString()}`);
    return;
  }

  if (sorting.state !== "relevance" || sorting.order !== "asc") {
    params.set("sortField", sorting.state);
    params.set("sortOrder", sorting.order);
  }

  if (isPrice) {
    params.set("priceMin", String(wizzyPriceState.currentMin));
    params.set("priceMax", String(wizzyPriceState.currentMax));
  }

  if (wizzyAttributes && Object.keys(wizzyAttributes).length > 0) {
    for (const key in wizzyAttributes) {
      if (!Array.isArray(wizzyAttributes[key]) || wizzyAttributes[key].length === 0) continue;
      params.set(`attr.${key}`, wizzyAttributes[key].join(","));
    }
  }

  if (arraysObj && Object.keys(arraysObj).length > 0) {
    for (const key in arraysObj) {
      if (!Array.isArray(arraysObj[key]) || arraysObj[key].length === 0) continue;
      params.set(`facet.${key}`, arraysObj[key].join(","));
    }
  }

  window.history.replaceState({}, "", `${currentUrl.pathname}?${params.toString()}`);
}

/**
 * Facet list for `/products/search` (stringified in the request body).
 */
function wizzyFacetsForSearchApi() {
  return Object.values(WIZZY_FACETS).sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * True when page > 1, filters, price, or non-default sort require `/products/filter`.
 */
function wizzyShouldUseFilterApi(wizzyAttributes, arraysObj, isPrice) {
  if (page.pageCount > 1) return true;
  if (isPrice) return true;
  if (Object.keys(wizzyAttributes || {}).length > 0) return true;
  if (Object.keys(arraysObj || {}).length > 0) return true;
  return sorting.state !== "relevance" || sorting.order !== "asc";
}

/**
 * Body for plain search (`/products/search`) — query only, default relevance sort.
 */
function buildWizzySearchBody(query, pageNum = 1) {
  return {
    q: query,
    currency: "INR",
    includeOutOfStock: "true",
    getAllVariants: "false",
    productsCount: WIZZY_PRODUCTS_COUNT,
    facets: JSON.stringify(wizzyFacetsForSearchApi()),
    swatch: JSON.stringify([{ key: "sizes" }]),
    minQueryLength: WIZZY_AUTOCOMPLETE_MIN_QUERY_LENGTH,
    sort: JSON.stringify([{ field: "relevance", order: "asc" }]),
    attributeFacetValuesLimit: 20,
    page: pageNum,
  };
}

/**
 * Reads searchedKey from the URL only when state is empty (reload / shared links).
 */
function wizzyReadSearchedKeyFromUrl() {
  if (wizzySearchedKey) return;
  const key = new URLSearchParams(window.location.search).get(WIZZY_SEARCHED_KEY_PARAM);
  if (typeof key === "string" && key.trim()) wizzySearchedKey = key.trim();
}

/**
 * Writes searchedKey into the current URL without dropping other params.
 */
function wizzyWriteSearchedKeyToUrl() {
  if (!isSearchPage() || modalRightPanel !== "search" || !searchQuery) return;

  const url = new URL(window.location.href);
  if (wizzySearchedKey) {
    if (url.searchParams.get(WIZZY_SEARCHED_KEY_PARAM) === wizzySearchedKey) return;
    url.searchParams.set(WIZZY_SEARCHED_KEY_PARAM, wizzySearchedKey);
  } else {
    if (!url.searchParams.has(WIZZY_SEARCHED_KEY_PARAM)) return;
    url.searchParams.delete(WIZZY_SEARCHED_KEY_PARAM);
  }

  const qs = url.searchParams.toString();
  window.history.replaceState({}, "", qs ? `${url.pathname}?${qs}` : url.pathname);
}

/**
 * Captures searchedKey once from the initial search response.
 * Never replaces an existing key (filter/sort responses keep the same key).
 * @param {object} response - Wizzy API response
 */
function wizzyCaptureSearchedKey(response) {
  if (wizzySearchedKey) return;
  const key = response?.payload?.filters?.searchedKey;
  if (typeof key !== "string" || !key) return;
  wizzySearchedKey = key;
  wizzyWriteSearchedKeyToUrl();
}

/**
 * Body for filtered/sorted results (`/products/filter`).
 * Reuses the same searchedKey from search pageload on every facet/sort request.
 */
function buildWizzyFilterBody(wizzyAttributes, arraysObj, isPrice) {
  const body = {
    sort: [{ field: sorting.state, order: sorting.order }],
    facets: WIZZY_FACETS,
    q: searchQuery,
    page: page.pageCount,
    productsCount: WIZZY_PRODUCTS_COUNT,
    currency: "INR",
    getAllVariants: "false",
    showOOSProductsInOrder: "false",
    attributeFacetValuesLimit: 20,
    attributes: wizzyAttributes,
    type: "DEFAULT",
    inStock: [],
    swatch: [{ key: "sizes" }],
    sellingPrice: isPrice ? [{ gte: wizzyPriceState.currentMin, lte: wizzyPriceState.currentMax }] : [],
  };

  // Same key as search pageload — required for relevant facet results.
  if (wizzySearchedKey) body.searchedKey = wizzySearchedKey;

  if (isSorting) body.origin = "SORT";

  if (Object.keys(arraysObj).length > 0) {
    for (const key in arraysObj) {
      body[key] = arraysObj[key];
    }
  }

  return body;
}

/**
 * Restores filters from URL params after page refresh.
 */
function applyRefreshFilters() {
  if (hasAppliedRefreshFilters) return false;

  const params = new URLSearchParams(window.location.search);

  const query = (params.get("q") || "").trim();
  if (query) searchQuery = query;

  wizzyReadSearchedKeyFromUrl();

  page.pageCount = 1;

  const sortField = (params.get("sortField") || "").trim();
  const sortOrder = (params.get("sortOrder") || "asc").trim();
  if (sortField) sorting.state = sortField;
  if (sortOrder === "asc" || sortOrder === "desc") sorting.order = sortOrder;
  isSorting = sorting.state !== "relevance" || sorting.order !== "asc";

  const restoredwizzyAttributes = {};
  const restoredArraysObj = {};

  params.forEach((value, key) => {
    if (!value) return;

    if (key.startsWith("attr.")) {
      const attrKey = key.replace("attr.", "");
      const values = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      if (values.length) restoredwizzyAttributes[attrKey] = values;
      return;
    }

    if (key.startsWith("facet.")) {
      const facetKey = key.replace("facet.", "");
      const values = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      if (values.length) restoredArraysObj[facetKey] = values;
    }
  });

  const priceMin = Number.parseFloat(params.get("priceMin"));
  const priceMax = Number.parseFloat(params.get("priceMax"));
  const hasPriceInUrl = !Number.isNaN(priceMin) && !Number.isNaN(priceMax);
  if (hasPriceInUrl) {
    wizzyPriceState.currentMin = priceMin;
    wizzyPriceState.currentMax = priceMax;
    isPrice = true;
  }

  wizzyAttributes = restoredwizzyAttributes;
  arraysObj = restoredArraysObj;

  const activeValues = [];
  Object.values(restoredwizzyAttributes).forEach((list) => activeValues.push(...list));
  Object.values(restoredArraysObj).forEach((list) => activeValues.push(...list));

  if (activeValues.length) {
    requestAnimationFrame(() => {
      const filterElements = document.querySelectorAll(".neo-filters-item[data-filter-key]");
      markActiveFilterElements(filterElements, activeValues);
    });
  }

  const hasNonDefaultSort = sortField && (sortField !== "relevance" || sortOrder !== "asc");
  const shouldApplyFilterFromUrl = hasPriceInUrl || Object.keys(restoredwizzyAttributes).length > 0 || Object.keys(restoredArraysObj).length > 0 || hasNonDefaultSort;

  hasAppliedRefreshFilters = true;
  if (shouldApplyFilterFromUrl && searchQuery) {
    sourceHitRequest = "filter";
    createFilterSearchQuery(wizzyAttributes, arraysObj, isPrice);
    return true;
  }

  return false;
}

/**
 * Central dispatcher for every /search-page product request — filter clicks,
 * sort changes, the price slider, infinite scroll, and page-load bootstrap
 * all call this. Builds the right request body and drives it through to a
 * rendered result:
 *   1. Bail out if the modal is mid-typing (autocomplete owns that flow) or
 *      there's no active query.
 *   2. Decide search-vs-filter API via `wizzyShouldUseFilterApi()` and build
 *      the matching request body.
 *   3. Mirror the new state into the URL off the main thread
 *      (`requestIdleCallback`) so it never blocks rendering.
 *   4. Try the cache waterfall (`wizzyTryApplyStoredProductResponse`) using
 *      the JSON-stringified body as the cache key — on a hit, done, no
 *      network call.
 *   5. On a miss, `sendWizzyProductRequest()` performs the fetch (or reuses
 *      an in-flight/prefetched one) and hydrates via
 *      `applyWizzyFilterResponse()`.
 */
function createFilterSearchQuery(wizzyAttributes, arraysObj, isPriceArg, autocompleteGen) {
  if (modalRightPanel === "input") return;
  if (!wizzyNormalizeQuery(searchQuery)) return;

  const priceActive = wizzyResolvePriceFilterActive(isPriceArg);
  isPrice = priceActive;

  const useFilterApi = wizzyShouldUseFilterApi(wizzyAttributes, arraysObj, priceActive);
  const apiMode = useFilterApi ? "filter" : "search";
  const body = useFilterApi ? buildWizzyFilterBody(wizzyAttributes, arraysObj, priceActive) : buildWizzySearchBody(searchQuery, page.pageCount);

  if (modalRightPanel === "search" && isSearchPage()) {
    requestIdleCallback(
      () => {
        addQueryInUrl(wizzyAttributes, arraysObj, priceActive);
      },
      { timeout: 200 },
    );
  }

  const requestKey = JSON.stringify(body);

  wizzyLogCache("createFilterSearchQuery", requestKey, {
    pageCount: page.pageCount,
    modalRightPanel,
    apiMode,
    priceActive,
    attributeKeys: Object.keys(wizzyAttributes || {}),
    facetKeys: Object.keys(arraysObj || {}),
  });

  if (wizzyTryApplyStoredProductResponse(requestKey, apiMode, autocompleteGen)) {
    return;
  }

  wizzyLogCache("MISS → will fetch (state changed or first load)", requestKey, {
    apiMode,
  });
  sendWizzyProductRequest(body, autocompleteGen, requestKey, apiMode);
}

function updateProductCount(count) {
  const productCountContainer = wizzyDom.productCountContainer;
  if (!productCountContainer) return;
  const productTextElement = productCountContainer.querySelector("span");
  if (!productTextElement) return;
  productTextElement.textContent = `${count} results found for "${searchQuery}"`;
}

/**
 * Hydrates the DOM from a search/filter API response — the single exit
 * point of `createFilterSearchQuery()`'s cache-hit and network-fetch paths
 * alike, so both are indistinguishable to the rest of the UI.
 * - Drops stale responses whose `autocompleteGen` no longer matches (query
 *   changed again while this one was in flight).
 * - `runSearchPageUi()` (facets, result count, page title, price bounds)
 *   only applies on the actual /search page, never in the typing modal.
 * - `skipCardRender` avoids re-rendering page-1 cards that are already on
 *   screen for this exact filter state (e.g. a response resolving for state
 *   that was already hydrated by a faster path).
 * - On the search page, both UI updates are deferred to the next paint
 *   (`requestAnimationFrame`) to batch DOM writes.
 */
function applyWizzyFilterResponse(response, autocompleteGen, hydrateSource) {
  if (autocompleteGen != null && autocompleteGen !== wizzyAutocompleteGeneration) return;

  wizzyCaptureSearchedKey(response);

  const products = response?.payload?.result || [];
  wizzyLogCache("HYDRATE UI", "", {
    source: hydrateSource || "unknown",
    pageCount: page.pageCount,
    productsThisPage: products.length,
    total: response?.payload?.total,
  });

  totalProductCount = response?.payload?.total || 0;
  page.payloadPageCount = response?.payload?.pages;

  const runSearchPageUi = () => {
    if (modalRightPanel !== "search" || !isSearchPage()) return;

    wizzyMarkSearchPageHydrated();
    updateProductCount(totalProductCount);

    filterFacetData(response.payload.facets);
    addFilterCount(response?.payload?.filters);

    const changePageTitleCount = document.querySelector("title");
    if (changePageTitleCount) {
      changePageTitleCount.textContent = `Search: ${response?.payload?.total} results found for ${searchQuery}`;
    }

    if (response?.payload?.filters?.sellingPrice?.length > 0) {
      wizzyPriceState.currentMin = response.payload.filters.sellingPrice[0].gte;
      wizzyPriceState.currentMax = response.payload.filters.sellingPrice[0].lte;
    }
  };

  const skipCardRender = page.pageCount === 1 && window.__wizzyLastRenderedProductKey === (wizzyActiveFilterState.filtersKey || "");

  const renderCards = () => {
    if (skipCardRender) {
      wizzyEnsureInfiniteScroll();
      return;
    }
    renderAutocompleteProducts(products);
    if (page.pageCount === 1) {
      window.__wizzyLastRenderedProductKey = wizzyActiveFilterState.filtersKey || "";
      window.__wizzySearchCardsRendered = true;
    }
  };

  if (modalRightPanel === "search") {
    requestAnimationFrame(() => {
      runSearchPageUi();
      renderCards();
    });
    return;
  }

  renderCards();
}

/**
 * Resolves one search/filter response, preferring the <head> prefetch over a
 * fresh network call whenever it exactly matches the request we're making
 * (same requestKey, apiMode, and normalized query) and we're on the /search
 * page. Preference order: already-resolved `window.__wizzyData` → the
 * in-flight prefetch promise → a live POST to the Wizzy API. This is what
 * lets the very first page load skip a duplicate fetch even though this
 * script starts building its own request independently of the inline
 * prefetch script.
 */
async function wizzyFetchProductResponse(query, requestKey, apiMode) {
  if (wizzyInFlightProductRequests.has(requestKey)) {
    wizzyLogCache("AWAIT in-flight request (duplicate call)", requestKey, {
      apiMode,
    });
    return wizzyInFlightProductRequests.get(requestKey);
  }

  const task = (async () => {
    const q = wizzyNormalizeQuery(query);
    const wd = window.__wizzyData;
    const pf = window.__wizzySearchPrefetch;
    const onSearchPage = modalRightPanel === "search" && isSearchPage();
    const prefetchMatches = pf && pf.requestKey === requestKey && (pf.apiMode || "filter") === apiMode && wizzyNormalizeQuery(pf.query) === q;

    if (wd && wd.data && !wd.consumed && onSearchPage && prefetchMatches) {
      wizzyLogCache("FETCH via __wizzyData (head prefetch)", requestKey, {
        apiMode,
      });
      return wd.data;
    }

    if (pf && pf.promise && onSearchPage && prefetchMatches) {
      try {
        wizzyLogCache("FETCH via head prefetch promise", requestKey, {
          apiMode,
        });
        const prefetched = await pf.promise;
        window.__wizzySearchPrefetch = null;
        return prefetched;
      } catch (_) {
        wizzyLogCache("Prefetch promise failed → live fetch", requestKey, {
          apiMode,
        });
      }
    }

    const endpoint = apiMode === "search" ? `${WIZZY_API_BASE}/products/search` : `${WIZZY_API_BASE}/products/filter`;

    const fetchBody = apiMode === "search" ? requestKey : JSON.stringify({ filters: requestKey, group: "PAGE" });

    wizzyLogCache("FETCH → network POST", requestKey, { apiMode, endpoint });

    const res = await fetch(endpoint, {
      method: "POST",
      headers: getWizzyApiHeaders(),
      body: fetchBody,
    });

    if (!res.ok) {
      throw new Error(`Wizzy HTTP ${res.status}`);
    }

    return res.json();
  })();

  wizzyInFlightProductRequests.set(requestKey, task);
  try {
    return await task;
  } finally {
    wizzyInFlightProductRequests.delete(requestKey);
  }
}

/**
 * Fetches search or filter results and hydrates the UI.
 * Cache lookup already happened in createFilterSearchQuery — this only runs on a miss.
 */
async function sendWizzyProductRequest(body, autocompleteGen, requestKey, apiMode) {
  const cacheKey = requestKey || JSON.stringify(body);

  const inFlight = wizzyInFlightProductRequests.get(cacheKey);
  if (inFlight) {
    wizzyLogCache("sendWizzyProductRequest AWAIT in-flight", cacheKey, {
      apiMode,
    });
    try {
      const response = await inFlight;
      if (response) {
        wizzyRememberFilterState(cacheKey, response, "in-flight", apiMode);
        applyWizzyFilterResponse(response, autocompleteGen, "in-flight");
      }
    } catch (error) {
      console.error("[wizzy-cache] in-flight error", error);
    }
    return;
  }

  createSkeletonLoaders();

  try {
    const response = await wizzyFetchProductResponse(searchQuery, cacheKey, apiMode);
    if (response) {
      wizzyRememberFilterState(cacheKey, response, "network", apiMode);
      applyWizzyFilterResponse(response, autocompleteGen, "network");
    }
  } catch (error) {
    console.error("[wizzy-cache] network error", error);
  }
}

function addFilterCount(filters) {
  if (!filters || typeof filters !== "object") return;

  showClearAll = 0;
  const keys = Object.keys(filters);

  const increaseCount = (key, value) => {
    const keyElement = document.querySelector(`[data-filter-key="${key}"]`);
    if (keyElement) {
      const countElement = keyElement.querySelector(".neo-filters-accordion-title-text-count");
      if (!countElement) return;

      const selectedCount = Array.isArray(value) ? value.length : 0;

      if (selectedCount > 0) {
        showClearAll += 1;
        countElement.innerText = selectedCount;
      } else {
        countElement.innerText = "";
      }
    }
  };

  document.querySelectorAll(".neo-filters-accordion-title-text-count").forEach((el) => {
    el.innerText = "";
  });

  keys.forEach((key) => {
    if (key === "attributes") {
      if (Object.keys(filters.attributes).length > 0) {
        const attributeKeys = Object.keys(filters.attributes);
        attributeKeys.forEach((atrKey) => {
          increaseCount(atrKey, filters.attributes[atrKey]);
        });
      }
    } else {
      increaseCount(key, filters[key]);
    }
  });

  const clearAllElment = wizzyDom.clearAllBtn;
  if (!clearAllElment) return;
  clearAllElment.classList.toggle("show", showClearAll > 0);
  clearAllElment.classList.toggle("hide", showClearAll <= 0);
}

/* ==========================================================================
      Filter UI (facets, accordions)
      ========================================================================== */

/**
 * Toggles a filter accordion open/closed.
 */
function openAccordion(event) {
  const title = event?.currentTarget || event?.target?.closest(".neo-filters-accordion-title");

  if (!title) return;

  const currentAccordion = title.closest(".neo-filters-accordion");
  const filterAccordions = document.querySelectorAll(".neo-filters-accordion");

  filterAccordions.forEach((accordion) => {
    if (accordion === currentAccordion) return;

    accordion.classList.remove("active");

    const accordionTitle = accordion.querySelector(".neo-filters-accordion-title");
    if (accordionTitle) {
      setTimeout(() => {
        accordionTitle.setAttribute("aria-expanded", "false");
      }, 0);
    }
  });

  if (!currentAccordion) return;

  const isActive = currentAccordion.classList.contains("active");
  currentAccordion.classList.toggle("active");

  setTimeout(() => {
    title.setAttribute("aria-expanded", !isActive);
  }, 0);
}

/** Only the first (preferred-gender) candidate is used here; only `<img onerror>` display:none fallback applies to failures. */
function wizzyGetFilterImageUrl(filterLabel, valueLabel) {
  return wizzyGetFilterImageUrlCandidates(filterLabel, valueLabel)[0];
}

/**
 * Builds candidate CDN image URLs for a filter swatch, ordered by gender
 * preference: the shopper's collection gender (from `config.collectionGender`
 * if present) first, then men, women, and a generic default, each de-duped.
 * Color swatches use a different CDN folder/slug (`colors-...`) than every
 * other facet type.
 */
function wizzyGetFilterImageUrlCandidates(filterLabel, valueLabel) {
  let cdnBaseUrl = "https://cdn.shopify.com/s/files/1/0655/9629/3317/files/";
  if (filterLabel === "Color" || filterLabel === "Colors") {
    cdnBaseUrl = "https://cdn.shopify.com/s/files/1/0764/2808/3500/files/";
    filterLabel = "colors";
  }

  const labelSlug = (filterLabel + "-" + valueLabel)
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/-+/g, "-")
    .replaceAll(/^-|-$/g, "");
  const preferredGender = ((typeof config !== "undefined" && config?.collectionGender) || "default").toLowerCase();
  const genderFallbackOrder = [preferredGender, "men", "women", "default"].filter((gender, index, arr) => arr.indexOf(gender) === index);

  return genderFallbackOrder.map((gender) => {
    return `${cdnBaseUrl}filter-image-${labelSlug}-${gender}.webp?width=100`;
  });
}

function getOpenedAccordions() {
  const opened = [];

  document.querySelectorAll(".neo-filters-accordion.active").forEach((acc) => {
    const title = acc.querySelector(".neo-filters-accordion-title");
    if (!title) return;
    const key = title.getAttribute("data-filter-key");
    if (key) opened.push(key);
  });

  return opened;
}

function restoreOpenedAccordions(openedAccordions = []) {
  const availableAccordions = new Set();

  document.querySelectorAll(".neo-filters-accordion-title").forEach((title) => {
    const key = title.getAttribute("data-filter-key");
    if (key) availableAccordions.add(key);
  });

  openedAccordions.forEach((key) => {
    if (!availableAccordions.has(key)) return;

    const accordion = document.querySelector(`.filter-${CSS.escape(key)}`);
    if (!accordion) return;

    accordion.classList.add("active");

    const title = accordion.querySelector(".neo-filters-accordion-title");
    if (title) {
      title.setAttribute("aria-expanded", "true");
    }
  });
}

/**
 * Rebuilds the entire filters sidebar from scratch on every search/filter
 * response (facet counts and available values change with each query), then
 * restores the UI state that a full rebuild would otherwise wipe: which
 * accordions were open (`getOpenedAccordions`/`restoreOpenedAccordions`) and
 * which values are checked (`restoreActiveFilters`, called after paint via
 * `requestAnimationFrame` since it needs the fresh DOM to exist first).
 */
function filterFacetData(facets) {
  const openedAccordions = getOpenedAccordions();
  const mainContainer = wizzyDom.filtersContainer;
  if (!mainContainer) return;
  mainContainer.innerHTML = "";

  const filterFragment = document.createDocumentFragment();

  facets.forEach((facet) => {
    // Hide price filter when there's no range to adjust (min === max).
    if (facet.label === "Price") {
      const min = Number(facet.data?.min);
      const max = Number(facet.data?.max);
      if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return;
    }

    const accordionContainer = createAccordion(facet);
    filterFragment.appendChild(accordionContainer);

    const accordionInner = accordionContainer.querySelector(".neo-filters-accordion-content-inner");
    const facetElementFragment = document.createDocumentFragment();
    const builder = FACET_BUILDERS[facet.label];

    if (builder) {
      if (facet.label === "Price") {
        facetElementFragment.appendChild(builder(facet.data));
      } else {
        facet.data.forEach((facetData) => {
          facetElementFragment.appendChild(builder(facetData));
        });
      }
    }

    accordionInner.appendChild(facetElementFragment);
  });

  mainContainer.appendChild(filterFragment);

  initFilterDelegation();

  requestAnimationFrame(() => {
    restoreActiveFilters();
    restoreOpenedAccordions(openedAccordions);
  });
}

function createAccordion(facet) {
  let productClass = null;

  if (facet.label == "Size") productClass = "size-flex";
  else if (facet.label == "Category") productClass = "size-grid_three";
  else productClass = "size-grid_one";

  const accordionDiv = document.createElement("div");
  const parentKey = "parentkey" in facet ? facet["parentkey"] : "";

  accordionDiv.classList.add("neo-filters-accordion", `filter-${facet.key}`);

  accordionDiv.innerHTML = `
            <div
              class="neo-filters-accordion-title"
              role="button"
              aria-expanded="false"
              aria-controls="filter-${facet.key}-content"
              data-filter-key="${facet.key}"
            >
              <div class="neo-title-count-container">
                <p class="neo-filters-accordion-title-text">${facet.label}</p>
                <p class="neo-filters-accordion-title-text-count"></p>
              </div>
      
              <span class="neo-filters-accordion-title-icon glass-effect relative">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <rect width="24" height="24" rx="12" transform="matrix(-4.37114e-08 1 1 4.37114e-08 1.04907e-06 0)" fill="black" fill-opacity="0.01"></rect> <line y1="-0.428571" x2="4.73552" y2="-0.428571" transform="matrix(-0.689877 0.723927 0.590343 0.807152 15.3477 10.2852)" stroke="white" stroke-width="0.857143"></line> <line y1="-0.428571" x2="4.6595" y2="-0.428571" transform="matrix(0.753163 0.657835 -0.519994 0.85417 8.57227 10.6484)" stroke="white" stroke-width="0.857143"></line> </svg>
              </span>
            </div>
      
            <div class="neo-filters-accordion-content"
                 id="filter-${facet.key}-content">
                  <div data-filter-key="${facet.key}" data-parent-key="${parentKey}"class="${facet.label === "Size" ? "size-product-grid" : ""} ${productClass} neo-filter-accordion-grid neo-filters-accordion-content-inner"></div>
            </div>
          `;

  return accordionDiv;
}

function createCategoryElement(facetData) {
  const imageUrl = wizzyGetFilterImageUrl("Category", facetData.key);

  const gridContainer = document.createElement("div");
  gridContainer.classList.add("neo-filters-item", "neo-filters-grid-item");
  gridContainer.setAttribute("data-filter-key", facetData.key);

  gridContainer.innerHTML = `
              <span class="neo-filters-clear-filter-icon glass-effect relative">
                  <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.707031 -3.09053e-08L2.85352 2.14648L5 -2.18557e-07L5.70703 0.707031L3.56055 2.85352L5.70703 5L5 5.70703L2.85352 3.56055L0.707031 5.70703L-3.09035e-08 5L2.14648 2.85352L-2.18557e-07 0.707031L0.707031 -3.09053e-08Z" fill="white"></path>
                  </svg>
              </span>
              <img
                  class="neo-filters-grid-item-image"
                  src="${imageUrl}"
                  alt="${facetData.key}"
                  width="100%"
                  height="100%"
                  onerror="this.style.display='none'"
              >
              <p class="neo-filters-grid-item-text">${facetData.label}</p>
          `;

  return gridContainer;
}

function createSizeElement(facetData) {
  const sizeContainer = document.createElement("div");
  sizeContainer.setAttribute("data-filter-key", facetData.key);
  sizeContainer.classList.add("neo-filters-item", "neo-filters-grid-item");

  sizeContainer.innerHTML = `
          <span class="neo-filters-clear-filter-icon glass-effect relative">
              <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.707031 -3.09053e-08L2.85352 2.14648L5 -2.18557e-07L5.70703 0.707031L3.56055 2.85352L5.70703 5L5 5.70703L2.85352 3.56055L0.707031 5.70703L-3.09035e-08 5L2.14648 2.85352L-2.18557e-07 0.707031L0.707031 -3.09053e-08Z" fill="white"></path>
              </svg>
          </span>
      
          <p class="neo-filters-grid-item-text">${facetData.label}</p>`;

  return sizeContainer;
}

function createColorElement(facetData) {
  const imageUrl = wizzyGetFilterImageUrl("Color", facetData.key);
  const encodedFilterLabel = encodeURIComponent("Color");
  const encodedValueLabel = encodeURIComponent(facetData.key);

  const imageCon = document.createElement("div");
  imageCon.classList.add("neo-filters-item", "neo-filters-flex-item");
  imageCon.setAttribute("data-filter-key", facetData.key);

  imageCon.innerHTML = `
              <div class="flex flex-row gap-5 items-center justify-start">
                  <img
                      class="neo-filters-flex-item-image"
                      src="${imageUrl}"
                      alt="${facetData.key}"
                      width="100%"
                      height="100%"
                      data-filter-label="${encodedFilterLabel}"
                      data-value-label="${encodedValueLabel}"
                      data-image-attempt="0"
                  >
                  <div style="flex: 1;">
                      <p class="neo-filters-flex-item-text">${facetData.label}</p>
                  </div>
              </div>
              <span class="neo-filters-color-checkbox" data-color="${facetData.key}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke="#e1ffe11a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                      <path d="M7.75 12L10.58 14.83L16.25 9.17" stroke="#e1ffe11a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
              </span>
          `;

  return imageCon;
}

function createDefaultElement(facetData, category) {
  const label = facetData.label || "";
  const imageUrl = wizzyGetFilterImageUrl(category, facetData.key) || "";

  const genderContainer = document.createElement("div");
  genderContainer.classList.add("neo-filters-item", `neo-filters-${label.replace(/\s+/g, "-").toLowerCase()}`, "hide-scrollbar", "neo-filters-flex-item");

  genderContainer.setAttribute("data-filter-key", facetData.key);
  genderContainer.setAttribute("role", "button");

  genderContainer.innerHTML = `
              <span class="neo-filters-clear-filter-icon glass-effect relative">
                  <svg width="6" height="6" viewBox="0 0 6 6" fill="none">
                      <path d="M0.707031 0L2.85352 2.14648L5 0L5.70703 0.707031L3.56055 2.85352L5.70703 5L5 5.70703L2.85352 3.56055L0.707031 5.70703L0 5L2.14648 2.85352L0 0.707031L0.707031 0Z" fill="white"></path>
                  </svg>
              </span>
              <img class="neo-filters-flex-item-image" src="${imageUrl}" alt="${label}" width="100%" height="100%">
              <div style="flex: 1;">
                  <p class="neo-filters-flex-item-text"></p>
              </div>
          `;

  genderContainer.querySelector(".neo-filters-flex-item-text").textContent = label;

  const img = genderContainer.querySelector("img");
  img.onerror = () => {
    img.style.display = "none";
  };

  return genderContainer;
}

/**
 * Clears all active filters and resets sort/pagination state.
 */
function clearAllFilterss() {
  wizzyPriceState.currentMin = wizzyPriceState.min;
  wizzyPriceState.currentMax = wizzyPriceState.max;

  const priceEl = wizzyDom.priceRange;
  if (priceEl) {
    priceEl.setAttribute("min-value", wizzyPriceState.currentMin);
    priceEl.setAttribute("max-value", wizzyPriceState.currentMax);
  }

  sorting.state = "relevance";
  sorting.order = "asc";

  page.payloadPageCount = 1;
  page.pageCount = 1;

  arraysObj = {};
  wizzyAttributes = {};
  totalProductCount = 0;
  isPrice = false;
  isSorting = false;
  sourceHitRequest = "search";
  showClearAll = 0;

  wizzyResetFilterUiState();
  createFilterSearchQuery(wizzyAttributes, arraysObj, false);

  const clearAll = wizzyDom.clearAllBtn;
  if (!clearAll) return;
  clearAll.classList.add("hide");
  clearAll.classList.remove("show");
}

function createFilterPrice(facetData) {
  wizzyPriceState.min = facetData.min;
  wizzyPriceState.max = facetData.max;
  wizzyPriceState.currentMin = facetData.min;
  wizzyPriceState.currentMax = facetData.max;

  const priceContainer = document.createElement("neo-price-range");
  priceContainer.setAttribute("min", `${facetData.min}`);
  priceContainer.setAttribute("max", `${facetData.max}`);
  priceContainer.setAttribute("initial-min", `${facetData.min}`);
  priceContainer.setAttribute("initial-max", `${facetData.max}`);

  return priceContainer;
}

/**
 * Toggles one facet value in a filter store (wizzyAttributes or arraysObj).
 */
function toggleFilterValue(store, key, value, isActive) {
  if (isActive) {
    if (!store[key]) store[key] = [];
    if (!store[key].includes(value)) store[key].push(value);
    return;
  }

  const list = store[key];
  if (!list) return;
  const index = list.indexOf(value);
  if (index > -1) list.splice(index, 1);
  if (list.length === 0) delete store[key];
}

/**
 * Toggles a facet filter and triggers a new search request. Bound via event
 * delegation from `initFilterDelegation()` (one listener on the whole
 * filters container, not one per item).
 * Resets to page 1 since the result set is changing. Whether the toggled
 * value goes into `wizzyAttributes` or `arraysObj` depends on the facet's
 * `data-parent-key` (set in `createAccordion()`): facets nested under a
 * parent attribute use `wizzyAttributes`, top-level facets use `arraysObj` —
 * they're sent as different shapes in `buildWizzyFilterBody()`.
 */
function createFilterRequest(event) {
  page.pageCount = 1;
  sourceHitRequest = "filter";

  const targetElement = event.target.classList.contains("neo-filters-item") ? event.target : event.target.closest(".neo-filters-item");
  if (!targetElement) return;

  const targetParentNode = targetElement.closest(".neo-filters-accordion-content-inner");
  const isActive = targetElement.classList.toggle("active");

  const checkBoxElement = targetElement.querySelector(".neo-filters-color-checkbox");
  if (checkBoxElement) {
    activateColorSvg(checkBoxElement.parentNode.parentNode);
  }

  const targetValue = targetElement.getAttribute("data-filter-key");
  const sendFilterKey = targetParentNode.getAttribute("data-filter-key");
  const parentKey = targetParentNode.getAttribute("data-parent-key");

  const store = parentKey ? wizzyAttributes : arraysObj;
  toggleFilterValue(store, sendFilterKey, targetValue, isActive);

  createFilterSearchQuery(wizzyAttributes, arraysObj, isPrice);
}

function activateColorSvg(parentColorCon) {
  const childContainers = parentColorCon.querySelectorAll(".neo-filters-item");

  childContainers.forEach((childCon) => {
    const isActive = childCon.classList.contains("active");
    const strokeColor = isActive ? "#ffffff" : "#e1ffe11a";
    childCon.querySelectorAll("svg path").forEach((path) => {
      path.setAttribute("stroke", strokeColor);
    });
  });
}

function restoreActiveFilters() {
  const activeValues = getActiveFilterValues();
  const filterElements = document.querySelectorAll(".neo-filters-item[data-filter-key]");
  markActiveFilterElements(filterElements, activeValues);
}

/* ==========================================================================
      Product rendering
      ========================================================================== */

/**
 * Delegates actual card markup to `window.WizzyProductCards.render()`
 * (from `snippets/wizzy-product-card-render.liquid`). Despite the name,
 * this renders both modal-autocomplete and /search-page results — the
 * modal case forces page 1 of 1 (no pagination/infinite-scroll skeleton in
 * the small preview list), the search-page case passes real page state and
 * wires `infiniteScrolling()` as the "need more pages" callback.
 */
function renderAutocompleteProducts(products) {
  const container = getProductGridContainer();
  if (!container || !window.WizzyProductCards) return;

  const isModalTyping = modalRightPanel === "input";

  window.WizzyProductCards.render(products, {
    container,
    pageCount: isModalTyping ? 1 : page.pageCount,
    payloadPageCount: isModalTyping ? 1 : page.payloadPageCount,
    skeletonStyle: WIZZY_SKELETON_STYLE,
    onNeedMorePages: isModalTyping ? undefined : infiniteScrolling,
  });
}

/* ==========================================================================
      Sort & mobile
      ========================================================================== */

function applySortFromLabel(sortingValue) {
  if (sortingValue === "Featured") {
    sorting.state = "relevance";
    sorting.order = "asc";
  } else if (sortingValue === "Low to High") {
    sorting.state = "sellingPrice";
    sorting.order = "asc";
  } else if (sortingValue === "High to Low") {
    sorting.state = "sellingPrice";
    sorting.order = "desc";
  }
}

// Runs immediately at parse time (script is loaded after the sort markup, not
// deferred to DOMContentLoaded) — wires the desktop sort dropdown's open/close
// and label-display behavior. Purely visual; the actual sort + re-fetch
// happens in `closeCustomOptionsWizzy()` below via `data-value` on each option.
document.querySelectorAll("[data-sort-select-wizzy]").forEach((dropdown) => {
  const trigger = dropdown.querySelector(".custom-select-trigger");
  const optionsBox = dropdown.querySelector(".custom-options");
  const selectedText = dropdown.querySelector(".selected-value");

  trigger.addEventListener("click", () => {
    optionsBox.style.display = optionsBox.style.display === "block" ? "none" : "block";
  });

  dropdown.querySelectorAll(".custom-option").forEach((option) => {
    option.addEventListener("click", () => {
      selectedText.textContent = option.textContent;
    });
  });

  document.addEventListener("click", (e) => {
    if (!dropdown.contains(e.target)) {
      optionsBox.style.display = "none";
    }
  });
});

/**
 * Applies a sort option from the desktop dropdown.
 */
function closeCustomOptionsWizzy(event) {
  event.target.parentNode.style.display = "none";
  const sortingValue = event.target.getAttribute("data-value");
  applySortFromLabel(sortingValue);

  isSorting = true;
  page.pageCount = 1;
  createFilterSearchQuery(wizzyAttributes, arraysObj, isPrice);
}

function openMobileSort() {
  const overlay = wizzyDom.mobileSortOverlay;
  const drawer = wizzyDom.mobileSortDrawer;
  if (overlay && drawer) {
    overlay.classList.add("active");
    drawer.classList.add("active");
    document.body.style.overflow = "hidden";
  }
}

function closeMobileSort() {
  const overlay = wizzyDom.mobileSortOverlay;
  const drawer = wizzyDom.mobileSortDrawer;
  if (overlay && drawer) {
    requestAnimationFrame(() => {
      overlay.classList.remove("active");
      drawer.classList.remove("active");
      document.body.style.overflow = null;
    });
  }
}

function applyMobileSort(event) {
  closeMobileSort();
  closeCustomOptionsWizzy(event);
}

function openFiltersModals() {
  wizzyDom.filterAside?.classList.add("active");
  document.body.style.overflow = "hidden";
}

function clsoeMobileFilters() {
  wizzyDom.filterAside?.classList.remove("active");
  document.body.style.overflow = null;
}

/* ==========================================================================
      Infinite scroll
      ========================================================================== */

/** True when the current page is the Wizzy `/search` results page. */
function isSearchPage() {
  return window.location.pathname.includes("/search");
}

/**
 * Append scroll sentinel + observer when prefetch rendered cards without wizzy-search.js.
 * Normally `WizzyProductCards.render()` appends its own skeleton sentinel and
 * calls `infiniteScrolling()` directly when more pages exist. This function
 * covers the case where cards for page 1 were rendered by the <head>
 * prefetch script (`wizzy-search-prefetch.liquid`) before this file had a
 * chance to run — it checks whether a sentinel is already there, creates one
 * if not, and attaches the observer so scrolling still loads page 2+.
 */
function wizzyEnsureInfiniteScroll() {
  const payloadPages = page.payloadPageCount || 1;
  if (page.pageCount >= payloadPages) return;

  const container = getProductGridContainer();
  if (!container) return;

  let trigger = getScrollSkeletonTrigger();

  if (!trigger && window.WizzyProductCards) {
    const skel = document.createElement("div");
    skel.classList.add("grid__item", "grid__item--skeleton");
    skel.innerHTML = `<skeleton style="${WIZZY_SKELETON_STYLE}"></skeleton>`;
    container.appendChild(skel);
    trigger = skel;
  }

  if (!trigger || window.__wizzyInfiniteScrollBound) return;

  infiniteScrolling();
}

/**
 * Watches the trailing skeleton placeholder with an IntersectionObserver;
 * once it scrolls into view, disconnects itself (one-shot — a new observer
 * is attached for the next page by whatever renders next), bumps
 * `page.pageCount`, and re-runs the normal filter/search flow. Passing
 * `page.pageCount > 1` through to `WizzyProductCards.render()` (in
 * `wizzy-product-card-render.liquid`) is what tells the renderer to append
 * new cards after removing the skeleton, instead of clearing the grid.
 */
function infiniteScrolling() {
  const trigger = getScrollSkeletonTrigger();

  if (!trigger) return;

  if (window.__wizzyInfiniteScrollObserver) {
    window.__wizzyInfiniteScrollObserver.disconnect();
  }

  // INP risk: low — observer only increments page and triggers cached/filter API; no layout reads.
  const observer = new IntersectionObserver(
    async (entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      window.__wizzyInfiniteScrollObserver = null;
      window.__wizzyInfiniteScrollBound = false;
      page.pageCount += 1;
      wizzyLogCache("infinite scroll → next page", "", {
        pageCount: page.pageCount,
      });
      await createFilterSearchQuery(wizzyAttributes, arraysObj, isPrice);
    },
    {
      root: null,
      threshold: 0.1,
    },
  );

  window.__wizzyInfiniteScrollObserver = observer;
  window.__wizzyInfiniteScrollBound = true;
  observer.observe(trigger);
}

/* ==========================================================================
      Global exports
      ========================================================================== */

window.clickOverlay = clickOverlay;
window.wizzyCloseSearchModalIfOpen = wizzyCloseSearchModalIfOpen;
window.handleClearAll = handleClearAll;
window.handleRemoveItem = handleRemoveItem;
window.openAccordion = openAccordion;
window.createFilterRequest = createFilterRequest;
window.clearAllFilterss = clearAllFilterss;
window.closeCustomOptionsWizzy = closeCustomOptionsWizzy;
window.openMobileSort = openMobileSort;
window.closeMobileSort = closeMobileSort;
window.applyMobileSort = applyMobileSort;
window.openFiltersModals = openFiltersModals;
window.clsoeMobileFilters = clsoeMobileFilters;
window.getInputValue = getInputValue;
window.GetInputValue = getInputValue;
window.createFilterSearchQuery = createFilterSearchQuery;
window.applyRefreshFilters = applyRefreshFilters;
window.ApplyRefreshFilters = applyRefreshFilters;

/* ==========================================================================
      /search page bootstrap IIFE
      ========================================================================== */

// Fetched once per page load, independent of any query — feeds the modal's
// trending-terms list (createTrendingList) until an actual autocomplete
// response supplies query-specific suggestions instead. This runs in <head>,
// ahead of the modal markup, so wizzyDom.trendingUl may not exist in the DOM
// yet — watch for it via MutationObserver instead of assuming it's there.
(async () => {
  trendingList = await fetchTrendingSearches();

  const renderTrendingList = (ul) => {
    wizzyDom.trendingUl = ul;
    createTrendingList(trendingList);
  };

  const existingUl = document.querySelector(".neo-search-trending-search-list");
  if (existingUl) {
    renderTrendingList(existingUl);
    return;
  }

  const trendingUlObserver = new MutationObserver(() => {
    const ul = document.querySelector(".neo-search-trending-search-list");
    if (!ul) return;
    trendingUlObserver.disconnect();
    renderTrendingList(ul);
  });
  trendingUlObserver.observe(document.documentElement, { childList: true, subtree: true });
})();

// Runs on every page (not just /search): sets up the header search icon and
// its dependent recent-search UI, which exist on all pages.
document.addEventListener("DOMContentLoaded", () => {
  initWizzyDomCache();
  initNeoSearchModal();
  renderRecentSearches();
  initRecentListDelegation();
  initFilterDelegation();
});

document.addEventListener("cart:updated", (e) => {
  requestAnimationFrame(() => {
    // Reload SmartWishlist after cart drawer re-render
    if (typeof resetModalTrendingList === 'function') {
      resetModalTrendingList();
    }
    // Reload SmartWishlist after cart drawer re-render
    if (typeof renderRecentSearches === 'function') {
      renderRecentSearches();
    }
  })
});

// Runs immediately (not gated on DOMContentLoaded) and only does anything on
// /search — this script tag sits after the results markup, so the DOM it
// needs already exists. It reconciles this file's state with whatever
// `wizzy-search-prefetch.liquid`'s inline <head> script already did before
// this file parsed, then falls back to a normal fetch if nothing usable was
// found.
(function () {
  if (!isSearchPage()) return;

  initWizzyDomCache();

  /**
   * Restores query/filter/sort state from the URL and gets the first page of
   * results on screen as cheaply as possible. Checked in this order, each
   * one short-circuiting the rest:
   *   1. `applyRefreshFilters()` — URL has non-default filters/sort/price
   *      (a reload or shared link) → restore state and fire a filter-API
   *      request for it directly.
   *   2. `window.__wizzyPendingProductRender` — the prefetch script's own
   *      MutationObserver fallback already rendered page-1 cards straight
   *      into the DOM (used when this file was slower to load than the
   *      container appearing); adopt that render as done.
   *   3. `window.__wizzyPendingHydration` — a response handed off by some
   *      other prefetch path that hasn't been applied through the normal
   *      hydration function yet → apply it now.
   *   4. `window.__wizzyData` (unconsumed) — the <head> prefetch's own fetch
   *      already resolved → hydrate directly from it, no network call.
   *   5. `wizzySearchPageAlreadyHydrated()` — cards are already on screen via
   *      one of the paths above → just make sure infinite scroll is wired.
   *   6. Nothing usable → fall through to `createFilterSearchQuery()`, which
   *      will hit the cache waterfall and then the network like normal.
   */
  function getSearchQuery() {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (!q) {
      window.location.href = "/";
      return;
    }
    searchQuery = q;
    modalRightPanel = "search";

    // Prefer URL searchedKey on reload, then seed from head prefetch if present.
    wizzyReadSearchedKeyFromUrl();
    wizzyCaptureSearchedKey(window.__wizzyData?.data);
    wizzyCaptureSearchedKey(window.__wizzyPendingProductRender?.response);
    wizzyCaptureSearchedKey(window.__wizzyPendingHydration?.response ?? window.__wizzyPendingHydration);

    if (applyRefreshFilters()) {
      wizzyEnsureInfiniteScroll();
      return;
    }

    // Seed pagination/total-count state from the prefetch even if a later
    // branch below ends up doing the actual rendering.
    const wdEarly = window.__wizzyData;
    if (wdEarly?.data?.payload) {
      page.payloadPageCount = wdEarly.data.payload.pages ?? page.payloadPageCount;
      totalProductCount = wdEarly.data.payload.total ?? totalProductCount;
    }

    // Prefetch script already rendered page 1 itself (this file loaded too
    // late to beat its own MutationObserver) — accept that render as-is
    // instead of re-rendering the same products.
    const pendingRender = window.__wizzyPendingProductRender;
    if (pendingRender?.response) {
      const container = wizzyDom.searchPrefetchContainer;
      if (container && window.WizzyProductCards) {
        const products = pendingRender.response?.payload?.result || [];
        window.WizzyProductCards.render(products, {
          container,
          pageCount: 1,
          payloadPageCount: pendingRender.response?.payload?.pages || 1,
        });
        window.__wizzySearchCardsRendered = true;
        window.__wizzyPendingProductRender = null;
      }
    }

    const pending = window.__wizzyPendingHydration;
    if (pending) {
      const response = pending.response != null ? pending.response : pending;
      const requestKey = pending.requestKey || pending.filtersKey || window.__wizzySearchPrefetch?.requestKey || window.__wizzySearchPrefetch?.filtersKey || "";
      const prefetchApiMode = pending.apiMode || window.__wizzySearchPrefetch?.apiMode || "search";
      window.__wizzyPendingHydration = null;
      if (response) {
        if (requestKey) {
          wizzyRememberFilterState(requestKey, response, "prefetch-pending", prefetchApiMode);
        }
        applyWizzyFilterResponse(response, null, "prefetch-pending");
        return;
      }
    }

    // The common happy path: the <head> prefetch's fetch already resolved
    // and nothing has consumed it yet — hydrate straight from it.
    const wd = window.__wizzyData;
    if (wd && wd.data != null && !wd.consumed) {
      const requestKey = window.__wizzySearchPrefetch?.requestKey || window.__wizzySearchPrefetch?.filtersKey || "";
      const prefetchApiMode = window.__wizzySearchPrefetch?.apiMode || "search";
      if (requestKey) {
        wizzyRememberFilterState(requestKey, wd.data, "__wizzyData", prefetchApiMode);
      }
      applyWizzyFilterResponse(wd.data, null, "__wizzyData");
      return;
    }

    if (wizzySearchPageAlreadyHydrated()) {
      wizzyLogCache("getSearchQuery SKIP (already hydrated via global prefetch state)", "");
      wizzyEnsureInfiniteScroll();
      return;
    }

    // Nothing else consumed it — allow a later retry to use it instead of
    // leaving it permanently marked "consumed" with nothing rendered.
    if (window.__wizzyData && window.__wizzyData.consumed && !wizzyActiveFilterState.response) {
      window.__wizzyData.consumed = false;
    }

    // No prefetch data usable at all (e.g. prefetch script didn't run, or
    // failed) — fall back to the standard fetch/cache flow.
    createFilterSearchQuery(wizzyAttributes, arraysObj, isPrice);
  }

  /**
   * Lets the mobile filters drawer be dragged down to dismiss, like a native
   * bottom sheet. Tracks touch position each frame to compute a translateY
   * percentage; on release, closes if the drag passed `CLOSE_THRESHOLD` of
   * the drawer's height or exceeded `VELOCITY_THRESHOLD` (a fast flick closes
   * even if it didn't travel far).
   */
  function initSearchFilterDrawerDrag() {
    const root = wizzyDom.searchProductGrid;
    const handle = root && root.querySelector(".filter_mobile-sort-handle");
    const panel = root && root.querySelector(".left-panel.neo-collection-aside");

    if (!handle || !panel) return;

    let isDragging = false;
    let startY = 0;
    let currentY = 0;
    let drawerHeight = 0;
    let animationFrameId = null;

    const CLOSE_THRESHOLD = 0.25;
    const VELOCITY_THRESHOLD = 0.2;
    let lastY = 0;
    let lastTime = 0;
    let velocity = 0;

    function updateTransform() {
      if (!isDragging) return;

      const dragDistance = Math.max(0, currentY - startY);
      const dragPercentage = drawerHeight ? dragDistance / drawerHeight : 0;
      const translateY = Math.min(100, dragPercentage * 100);

      panel.style.transform = `translateY(${translateY}%)`;

      const now = performance.now();
      if (lastTime > 0) {
        const timeDelta = now - lastTime;
        const yDelta = currentY - lastY;
        velocity = timeDelta > 0 ? yDelta / timeDelta : 0;
      }
      lastY = currentY;
      lastTime = now;

      animationFrameId = requestAnimationFrame(updateTransform);
    }

    function handleTouchStart(e) {
      if (!panel.classList.contains("active")) return;
      if (e.target.closest("button")) return;

      const touch = e.touches[0];
      startY = touch.clientY;
      currentY = startY;
      lastY = startY;
      lastTime = performance.now();
      velocity = 0;

      drawerHeight = panel.offsetHeight;

      panel.style.transition = "none";

      isDragging = true;

      e.preventDefault();

      updateTransform();
    }

    function handleTouchMove(e) {
      if (!isDragging) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - startY;

      if (deltaY > 0) {
        e.preventDefault();
      }

      currentY = touch.clientY;
    }

    function handleTouchEnd() {
      if (!isDragging) return;

      isDragging = false;

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }

      const dragDistance = currentY - startY;
      const dragPercentage = drawerHeight ? dragDistance / drawerHeight : 0;

      panel.style.transition = "";

      const shouldClose = dragPercentage > CLOSE_THRESHOLD || velocity > VELOCITY_THRESHOLD;

      if (shouldClose) {
        if (typeof clsoeMobileFilters === "function") clsoeMobileFilters();
        setTimeout(() => {
          panel.style.transform = "";
        }, 450);
      } else {
        panel.style.transform = "translateY(0%)";
      }

      lastY = 0;
      lastTime = 0;
      velocity = 0;
    }

    function handleTouchCancel() {
      handleTouchEnd();
    }

    handle.addEventListener("touchstart", handleTouchStart, { passive: false });
    handle.addEventListener("touchmove", handleTouchMove, { passive: false });
    handle.addEventListener("touchend", handleTouchEnd, { passive: true });
    handle.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
    });
  }

  // Fired by the `<neo-price-range>` custom element (created in
  // `createFilterPrice()`) whenever the shopper drags the slider. Treated
  // like any other filter change: reset to page 1 and re-run the query.
  document.addEventListener("neo:price-range-change", (event) => {
    if (typeof createFilterSearchQuery !== "function") return;
    const detail = event && event.detail;
    const minPrice = detail && detail.minPrice;
    const maxPrice = detail && detail.maxPrice;

    wizzyPriceState.currentMin = minPrice;
    wizzyPriceState.currentMax = maxPrice;

    isPrice = true;
    page.pageCount = 1;
    modalRightPanel = "search";
    createFilterSearchQuery(wizzyAttributes, arraysObj, isPrice);
  });

  if (typeof createFilterSearchQuery !== "function") {
    console.error("[wizzy-search] createFilterSearchQuery is missing. Ensure assets/wizzy-search.js is loaded intact (modal + search bootstrap).");
    return;
  }

  getSearchQuery();
  wizzyEnsureInfiniteScroll();
  initSearchFilterDrawerDrag();
})();
